import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Box, Container, Typography, TextField, Grid, Card, CardContent, CircularProgress, Button, Tabs, Tab, Alert, Chip, Paper, Popper, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { getPartnerCatalogPath } from '../utils/partnerCatalogStorage';
import { getCheapPricesFromOrigin, groupDealsByContinent } from '../services/travelpayouts';
// events service no longer needed — using event-guides API directly
import { getCityName } from '../utils/iataToCity';
import { loadGoogleMaps } from '../utils/googleMapsLoader';
import travelService from '../services/travelService';
import viatorService from '../services/viator';
import googleFlightsService from '../services/googleFlights';
import packageService from '../services/packageService';
import packageBuilderService from '../services/packageBuilderService';
import { Autocomplete } from '@mui/material';
import { AIRPORT_DATABASE } from '../utils/airportDatabase';
import SEOHead from '../components/SEOHead';
import RoadTripPlanner from '../components/RoadTripPlanner';
import StayListingCard from '../components/StayListingCard';
import StaysMapView, { isListingInMapBounds } from '../components/StaysMapView';
import { getCityImage, DEFAULT_CITY_IMAGE } from '../utils/cityImages';
import RefreshIcon from '@mui/icons-material/Refresh';
import MapIcon from '@mui/icons-material/Map';
import ViewListIcon from '@mui/icons-material/ViewList';
import NumericTextField from '../components/NumericTextField';
import DocumentMarketingHero from '../components/DocumentMarketingHero';
import { DOCUMENT, documentPageCanvasSx, documentChromeTabsSx, documentFilterChipSx, documentToggleBtnSx } from '../constants/documentTheme';
import { partnerOxbloodBtnSx, partnerOutlineBtnSx } from '../components/PartnerListChrome';
import DocumentPackageCard from '../components/DocumentPackageCard';
import MarketplacePackageCard from '../components/MarketplacePackageCard';
import DocumentPageOverlay from '../components/DocumentPageOverlay';
import PublicPackageDetails from './PublicPackageDetails';
import PackageAgentFilter from '../components/PackageAgentFilter';
import AgentDetailsDialog from '../components/AgentDetailsDialog';
import AdvisorInquiryForm from '../components/AdvisorInquiryForm';
import { useAuth } from '../contexts/AuthContext';
import usePrivateTravelAvailable from '../hooks/usePrivateTravelAvailable';
import { filterPackagesByAgent, uniquePackageAgents } from '../utils/packageAgentFilter';

const DEFAULT_ORIGIN = '';

// --- Default landing page data ---

const FLIGHT_PROMO_CARDS = [
  { title: 'Last-Minute Getaways', subtitle: 'Sometimes the best things in life are unplanned', cta: 'See deals', image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=600&h=400&fit=crop&q=80' },
  { title: 'All-Inclusive Escapes', subtitle: 'The rewards never end at an all-inclusive resort', cta: 'Book now', image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=600&h=400&fit=crop&q=80' },
  { title: 'Budget-Friendly Flights', subtitle: 'Fly somewhere new without breaking the bank', cta: 'Find flights', image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=600&h=400&fit=crop&q=80' },
  { title: 'Weekend Escapes', subtitle: 'Fly somewhere beachy, sunny and warm', cta: 'Explore', image: 'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?w=600&h=400&fit=crop&q=80' },
];

const POPULAR_FLIGHT_ROUTES = [
  { from: 'New York', fromCode: 'JFK', to: 'London', toCode: 'LHR', price: '$350', image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=400&h=250&fit=crop&q=80' },
  { from: 'Los Angeles', fromCode: 'LAX', to: 'Tokyo', toCode: 'NRT', price: '$520', image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=400&h=250&fit=crop&q=80' },
  { from: 'Chicago', fromCode: 'ORD', to: 'Paris', toCode: 'CDG', price: '$420', image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=400&h=250&fit=crop&q=80' },
  { from: 'Miami', fromCode: 'MIA', to: 'Cancun', toCode: 'CUN', price: '$180', image: 'https://images.unsplash.com/photo-1510097467424-192d713fd8b2?w=400&h=250&fit=crop&q=80' },
  { from: 'San Francisco', fromCode: 'SFO', to: 'Barcelona', toCode: 'BCN', price: '$480', image: 'https://images.unsplash.com/photo-1583422409516-2895a77efded?w=400&h=250&fit=crop&q=80' },
  { from: 'Dallas', fromCode: 'DFW', to: 'Rome', toCode: 'FCO', price: '$440', image: 'https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=400&h=250&fit=crop&q=80' },
  { from: 'Seattle', fromCode: 'SEA', to: 'Bali', toCode: 'DPS', price: '$650', image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=400&h=250&fit=crop&q=80' },
  { from: 'Boston', fromCode: 'BOS', to: 'Dubai', toCode: 'DXB', price: '$580', image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=400&h=250&fit=crop&q=80' },
];

const POPULAR_STAY_DESTINATIONS = [
  { city: 'Paris', country: 'France', image: getCityImage('Paris'), tag: 'Romance' },
  { city: 'London', country: 'UK', image: getCityImage('London'), tag: 'Culture' },
  { city: 'Tokyo', country: 'Japan', image: getCityImage('Tokyo'), tag: 'Adventure' },
  { city: 'New York', country: 'USA', image: getCityImage('New York'), tag: 'City' },
  { city: 'Rome', country: 'Italy', image: getCityImage('Rome'), tag: 'History' },
  { city: 'Barcelona', country: 'Spain', image: getCityImage('Barcelona'), tag: 'Beach' },
  { city: 'Dubai', country: 'UAE', image: getCityImage('Dubai'), tag: 'Luxury' },
  { city: 'Bali', country: 'Indonesia', image: getCityImage('Bali'), tag: 'Tropical' },
  { city: 'Cancun', country: 'Mexico', image: getCityImage('Cancun'), tag: 'All-Inclusive' },
  { city: 'Las Vegas', country: 'USA', image: getCityImage('Las Vegas'), tag: 'Entertainment' },
];

// Stays providers loaded first (fast hotel APIs + reliable scrapers) vs. in the
// background (bot-blocked sites surfaced as instant "search directly" links).
const STAYS_PRIMARY_PROVIDERS = ['googlehotels', 'googlerentals', 'liteapi', 'liteapirentals', 'impt', 'airbnb', 'booking'];
const STAYS_SECONDARY_PROVIDERS = ['vrbo', 'redweek', 'orbitz', 'hotels'];

const POPULAR_ACTIVITY_DESTINATIONS = [
  { city: 'Paris', country: 'France', image: 'https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=400&h=300&fit=crop&q=80', tags: ['Museums', 'Food Tours', 'Walking Tours'] },
  { city: 'Tokyo', country: 'Japan', image: 'https://images.unsplash.com/photo-1503899036084-c55cdd92da26?w=400&h=300&fit=crop&q=80', tags: ['Cultural', 'Food Tours', 'Temple Visits'] },
  { city: 'Rome', country: 'Italy', image: 'https://images.unsplash.com/photo-1529260830199-42c24126f198?w=400&h=300&fit=crop&q=80', tags: ['History', 'Food Tours', 'Walking Tours'] },
  { city: 'New York', country: 'USA', image: 'https://images.unsplash.com/photo-1534430480872-3498386e7856?w=400&h=300&fit=crop&q=80', tags: ['Shows', 'Sightseeing', 'Food Tours'] },
  { city: 'Barcelona', country: 'Spain', image: 'https://images.unsplash.com/photo-1562883676-8c7feb83f09b?w=400&h=300&fit=crop&q=80', tags: ['Architecture', 'Beach', 'Nightlife'] },
  { city: 'London', country: 'UK', image: 'https://images.unsplash.com/photo-1486299267070-83823f5448dd?w=400&h=300&fit=crop&q=80', tags: ['Museums', 'Theatre', 'Walking Tours'] },
  { city: 'Bali', country: 'Indonesia', image: 'https://images.unsplash.com/photo-1555400038-63f5ba517a47?w=400&h=300&fit=crop&q=80', tags: ['Water Sports', 'Temples', 'Cooking Class'] },
  { city: 'Cancun', country: 'Mexico', image: 'https://images.unsplash.com/photo-1552074284-5e88ef1aef18?w=400&h=300&fit=crop&q=80', tags: ['Snorkeling', 'Ruins', 'Beach'] },
];

const ACTIVITY_CATEGORIES = [
  { label: 'Food & Drink', icon: '🍷' },
  { label: 'Tours & Sightseeing', icon: '🏛️' },
  { label: 'Outdoor Adventures', icon: '🏔️' },
  { label: 'Cultural Experiences', icon: '🎭' },
  { label: 'Water Sports', icon: '🤿' },
  { label: 'Nightlife', icon: '🌃' },
  { label: 'Classes & Workshops', icon: '👨‍🍳' },
  { label: 'Day Trips', icon: '🚌' },
];

// Helper function to convert IATA code to city name
const iataToCity = (code) => {
  if (!code || code.length !== 3) return code;
  const upperCode = code.toUpperCase().trim();
  const airport = AIRPORT_DATABASE.find(a => a.iata === upperCode);
  return airport ? airport.city : code;
};

const resolveStaySearchLocation = (input, options = []) => {
  const trimmed = (input || '').trim();
  if (!trimmed) {
    return trimmed;
  }

  if (trimmed.length === 3 && trimmed === trimmed.toUpperCase()) {
    return getCityName(trimmed);
  }

  const lower = trimmed.toLowerCase();
  const matchingOption = options.find((opt) => {
    const label = (opt.label || opt.value || '').toLowerCase();
    return label.startsWith(lower) || label.includes(lower);
  });

  if (matchingOption) {
    if (matchingOption.city?.trim()) {
      return matchingOption.city.trim();
    }
    const label = matchingOption.label || matchingOption.value || '';
    return label.split(',')[0].trim();
  }

  if (trimmed.includes(',')) {
    return trimmed.split(',')[0].trim();
  }

  return trimmed;
};

// Booking.com fallback for hotels
const getBookingFallback = (destination, checkIn = null, checkOut = null, guests = 1) => {
  // Format dates for booking.com (YYYY-MM-DD)
  const checkinDate = checkIn || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const checkoutDate = checkOut || new Date(Date.now() + 11 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
  const adults = guests || 1;
  
  // Build booking.com URL parameters
  const buildBookingUrl = (city, classFilter = '') => {
    const params = new URLSearchParams({
      ss: city,
      checkin: checkinDate,
      checkout: checkoutDate,
      group_adults: adults.toString(),
      no_rooms: '1',
      utm_source: 'dejavu-nomad',
      utm_medium: 'affiliate',
      utm_campaign: 'hotels'
    });
    if (classFilter) {
      params.append('nflt', classFilter);
    }
    return `https://www.booking.com/searchresults.html?${params.toString()}`;
  };
  
  if (destination === 'general') {
    // Return 10 general hotel deals for top destinations
    return [
      {
        id: 'general_1',
        name: 'Luxury Hotels in Paris',
        location: 'Paris, France',
        rating: 4.8,
        amenities: ['WiFi', 'Pool', 'Spa', 'Restaurant'],
        price: 'From $200',
        currency: 'USD',
        image: getHotelImage('Paris'),
        booking_url: buildBookingUrl('Paris', 'class%3D5'),
        source: 'booking'
      },
      {
        id: 'general_2',
        name: 'Boutique Hotels in London',
        location: 'London, UK',
        rating: 4.6,
        amenities: ['WiFi', 'Gym', 'Bar', 'Room Service'],
        price: 'From $180',
        currency: 'USD',
        image: getHotelImage('London'),
        booking_url: buildBookingUrl('London', 'class%3D4'),
        source: 'booking'
      },
      {
        id: 'general_3',
        name: 'Resort Hotels in Tokyo',
        location: 'Tokyo, Japan',
        rating: 4.7,
        amenities: ['WiFi', 'Spa', 'Restaurant', 'Concierge'],
        price: 'From $220',
        currency: 'USD',
        image: getHotelImage('Tokyo'),
        booking_url: buildBookingUrl('Tokyo', 'class%3D5'),
        source: 'booking'
      },
      {
        id: 'general_4',
        name: 'Beach Hotels in Miami',
        location: 'Miami, USA',
        rating: 4.5,
        amenities: ['WiFi', 'Pool', 'Spa', 'Beach Access'],
        price: 'From $160',
        currency: 'USD',
        image: getHotelImage('Miami'),
        booking_url: buildBookingUrl('Miami', 'class%3D4'),
        source: 'booking'
      },
      {
        id: 'general_5',
        name: 'Historic Hotels in Rome',
        location: 'Rome, Italy',
        rating: 4.4,
        amenities: ['WiFi', 'Spa', 'Restaurant', 'Concierge'],
        price: 'From $140',
        currency: 'USD',
        image: getHotelImage('Rome'),
        booking_url: buildBookingUrl('Rome', 'class%3D4'),
        source: 'booking'
      },
      {
        id: 'general_6',
        name: 'Modern Hotels in Dubai',
        location: 'Dubai, UAE',
        rating: 4.9,
        amenities: ['WiFi', 'Pool', 'Spa', 'Beach Access'],
        price: 'From $300',
        currency: 'USD',
        image: getHotelImage('Dubai'),
        booking_url: buildBookingUrl('Dubai', 'class%3D5'),
        source: 'booking'
      },
      {
        id: 'general_7',
        name: 'Luxury Hotels in New York',
        location: 'New York, USA',
        rating: 4.7,
        amenities: ['WiFi', 'Gym', 'Concierge', 'Room Service'],
        price: 'From $250',
        currency: 'USD',
        image: getHotelImage('New York'),
        booking_url: buildBookingUrl('New York', 'class%3D5'),
        source: 'booking'
      },
      {
        id: 'general_8',
        name: 'Resort Hotels in Bali',
        location: 'Bali, Indonesia',
        rating: 4.6,
        amenities: ['WiFi', 'Pool', 'Spa', 'Beach Access'],
        price: 'From $120',
        currency: 'USD',
        image: getHotelImage('Bali'),
        booking_url: buildBookingUrl('Bali', 'class%3D4'),
        source: 'booking'
      },
      {
        id: 'general_9',
        name: 'Boutique Hotels in Barcelona',
        location: 'Barcelona, Spain',
        rating: 4.5,
        amenities: ['WiFi', 'Gym', 'Bar', 'Room Service'],
        price: 'From $130',
        currency: 'USD',
        image: getHotelImage('Barcelona'),
        booking_url: buildBookingUrl('Barcelona', 'class%3D4'),
        source: 'booking'
      },
      {
        id: 'general_10',
        name: 'Historic Hotels in Prague',
        location: 'Prague, Czech Republic',
        rating: 4.3,
        amenities: ['WiFi', 'Spa', 'Restaurant', 'Concierge'],
        price: 'From $90',
        currency: 'USD',
        image: getHotelImage('Prague'),
        booking_url: buildBookingUrl('Prague', 'class%3D4'),
        source: 'booking'
      }
    ];
  }
  
  // Return specific destination hotels with different images for each type
  return [
    {
      id: 'booking_1',
      name: `Hotels in ${destination}`,
      location: destination,
      rating: 4.2,
      amenities: ['WiFi', 'Pool', 'Spa', 'Restaurant'],
      price: 'From $80',
      currency: 'USD',
      image: getHotelImage(destination, 'general'),
      booking_url: buildBookingUrl(destination),
      source: 'booking'
    },
    {
      id: 'booking_2',
      name: `Luxury Hotels in ${destination}`,
      location: destination,
      rating: 4.5,
      amenities: ['WiFi', 'Spa', 'Concierge', 'Restaurant'],
      price: 'From $150',
      currency: 'USD',
      image: getHotelImage(destination, 'luxury'),
      booking_url: buildBookingUrl(destination, 'class%3D5'),
      source: 'booking'
    },
    {
      id: 'booking_3',
      name: `Budget Hotels in ${destination}`,
      location: destination,
      rating: 4.0,
      amenities: ['WiFi', 'Gym', 'Bar'],
      price: 'From $50',
      currency: 'USD',
      image: getHotelImage(destination, 'budget'),
      booking_url: buildBookingUrl(destination, 'class%3D2'),
      source: 'booking'
    }
  ];
};

// Get default dates (next week for departure, 2 weeks for return)
const getDefaultDates = () => {
  const today = new Date();
  const departureDate = new Date(today);
  departureDate.setDate(today.getDate() + 7); // 1 week from now
  const returnDateObj = new Date(today);
  returnDateObj.setDate(today.getDate() + 14); // 2 weeks from now
  
  const formatYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  
  return {
    departure: formatYMD(departureDate),
    return: formatYMD(returnDateObj)
  };
};

const getDestinationImage = (destination) => getCityImage(destination);

const getHotelImage = (destination) => getCityImage(destination, DEFAULT_CITY_IMAGE);

const DealsPlan = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const defaultDates = useMemo(() => getDefaultDates(), []);
  
  // Check if we're returning from a flight details page with saved search params
  const urlOrigin = searchParams.get('origin');
  const urlDestination = searchParams.get('destination');
  const urlDepartDate = searchParams.get('departDate');
  const urlReturnDate = searchParams.get('returnDate');
  const urlPassengers = searchParams.get('passengers');
  const urlTripType = searchParams.get('tripType');
  const urlSortBy = searchParams.get('sortBy');
  
  const [origin, setOrigin] = useState(urlOrigin || DEFAULT_ORIGIN);
  const [destination, setDestination] = useState(urlDestination || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [groupedDeals, setGroupedDeals] = useState({});
  const [events, setEvents] = useState([]);
  // Events tab tracks whether user explicitly searched (vs default "show all")
  // const [activities, setActivities] = useState([]); // Replaced with viatorActivities
  const [viatorCityUrl, setViatorCityUrl] = useState('');
  const [originOptions, setOriginOptions] = useState([]);
  const [destinationOptions, setDestinationOptions] = useState([]);
  const [destinationSuggestionsOpen, setDestinationSuggestionsOpen] = useState(false);
  const [destinationAutocompleteOpen, setDestinationAutocompleteOpen] = useState(false);
  const stayDestinationInputRef = useRef(null);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [departDate, setDepartDate] = useState(urlDepartDate || defaultDates.departure);
  const [returnDate, setReturnDate] = useState(urlReturnDate || defaultDates.return);
  const [passengers, setPassengers] = useState(urlPassengers ? parseInt(urlPassengers, 10) : 1);
  const [staysRadiusMiles, setStaysRadiusMiles] = useState(20);
  const [tripType, setTripType] = useState(urlTripType || 'round_trip'); // one_way, round_trip
  // eslint-disable-next-line no-unused-vars
  const [imageMap, setImageMap] = useState({}); 
  const [activeTab, setActiveTab] = useState(6);
  const [destinationsData, setDestinationsData] = useState([]);
  const [destinationsContinents, setDestinationsContinents] = useState([]);
  const [destinationsLoading, setDestinationsLoading] = useState(false);
  const [destinationsSearch, setDestinationsSearch] = useState('');
  const [destinationsContinent, setDestinationsContinent] = useState('');
  const [sortBy] = useState(urlSortBy || 'price'); // Kept for sortedFlights useMemo
  // eslint-disable-next-line no-unused-vars
  const [showAllFlights] = useState(false); // Kept for compatibility
  const [shouldRestoreSearch, setShouldRestoreSearch] = useState(false);
  
  // Vacation packages state
  const [vacationPackages, setVacationPackages] = useState([]);
  
  // Partner/Agent packages state
  const [partnerPackages, setPartnerPackages] = useState([]);
  const [partnerPackagesLoading, setPartnerPackagesLoading] = useState(false);
  const { user } = useAuth();
  const { available: privateTravelAvailable } = usePrivateTravelAvailable();
  const [packagesSubTab, setPackagesSubTab] = useState('agent'); // 'agent' | 'private' | 'build'
  const [packagesInitialLoad, setPackagesInitialLoad] = useState(false); // Track if we've done initial load
  const [selectedPackageAgentId, setSelectedPackageAgentId] = useState('');
  const [packageAgentDetails, setPackageAgentDetails] = useState(null);
  const [previewPackage, setPreviewPackage] = useState(null);
  const [privatePackages, setPrivatePackages] = useState([]);
  const [privatePackagesLoading, setPrivatePackagesLoading] = useState(false);
  const [privateRequestOpen, setPrivateRequestOpen] = useState(false);
  const [privateRequestTarget, setPrivateRequestTarget] = useState(null);
  const [privateRequestBusy, setPrivateRequestBusy] = useState(false);
  const packageAgents = useMemo(() => uniquePackageAgents(partnerPackages), [partnerPackages]);
  const visiblePartnerPackages = useMemo(
    () => filterPackagesByAgent(partnerPackages, selectedPackageAgentId),
    [partnerPackages, selectedPackageAgentId]
  );
  
  const formatYMD = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const computeRange = useCallback(() => {
    const now = new Date();
    // Use tomorrow as default start date to ensure it's in the future
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    let start = departDate || formatYMD(tomorrow);
    let end = returnDate;
    
    // If no return date provided, use a reasonable default (7 days from start)
    if (!end) {
      const startDate = new Date(start);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 7); // 7 days from start date
      end = formatYMD(endDate);
    }
    
    return { start, end };
  }, [departDate, returnDate]);

  // Seed destinations when no origin is selected (general top destinations per continent)
  const continentSeeds = useMemo(() => ({
    'Africa': ['Cape Town','Nairobi','Marrakesh','Cairo'],
    'Asia': ['Tokyo','Bangkok','Singapore','Bali'],
    'Europe': ['Paris','London','Rome','Barcelona'],
    'North America': ['New York','Los Angeles','Toronto','Cancun'],
    'South America': ['Rio de Janeiro','Buenos Aires','Lima','Santiago'],
    'Oceania': ['Sydney','Auckland','Melbourne','Fiji']
  }), []);

  const loadDeals = useCallback(async (currentOrigin) => {
    // Load deals for origin
    
    try {
      setLoading(true);
      setError(null);
      let grouped = {};
      
      if (currentOrigin && currentOrigin.trim().length === 3) {
        const cheapData = await getCheapPricesFromOrigin(currentOrigin.toUpperCase(), 'usd', passengers);
        
        grouped = await groupDealsByContinent(cheapData);
        // Grouped deals by continent
      } else {
        // No valid origin, showing general deals
        // No origin: build general rows from seeds with sample prices
        const seedGrouped = {};
        const samplePrices = {
          'Africa': 450,
          'Asia': 380,
          'Europe': 520,
          'North America': 480,
          'South America': 420,
          'Oceania': 650
        };
        Object.entries(continentSeeds).forEach(([label, list]) => {
          const basePrice = samplePrices[label] || 500;
          seedGrouped[label] = list.map((name, index) => ({ 
            destinationName: name, 
            destination: name.toUpperCase().slice(0, 3), 
            price: basePrice + (index * 50) // Vary prices slightly
          }));
        });
        grouped = seedGrouped;
        // Default flights removed - users search for flights on-demand
        console.log('ℹ️ Flight deals displayed from TravelPayouts API');
      }
      setGroupedDeals(grouped);

      // Load Duffel flights and hotels if we have destination (dates are optional)
      if (destination) {
        try {
          setDuffelLoading(true);
          const { start, end } = computeRange();
          
          // Search for flights if we have origin
          if (currentOrigin) {
            // Convert city names to IATA codes for flight search
            const originIata = await travelService.getIATACode(currentOrigin);
            const destinationIata = await travelService.getIATACode(destination);
            
            // Priority 1: Try Duffel first (always prioritized)
            console.log('✈️ Fetching flights from Duffel (priority)...');
            let duffelFlights = await travelService.getFlightRecommendations({
              origin: originIata,
              destination: destinationIata,
              departure_date: start,
              return_date: end,
              passengers: passengers,
              cabin_class: 'economy'
            });

            // Ensure Duffel flights are marked with source
            duffelFlights = (duffelFlights || []).map(flight => ({
              ...flight,
              source: 'duffel',
              priority: 1
            }));

            console.log(`✅ Duffel returned ${duffelFlights.length} flights (priority)`);

            let allFlights = [...duffelFlights];

            // Fallback to Travelpayouts ONLY if Duffel returns no flights
            if (duffelFlights.length === 0) {
              console.log('⚠️ Duffel returned no flights, trying Travelpayouts fallback...');
              try {
                const cheapData = await getCheapPricesFromOrigin(originIata, 'usd', passengers);
                let fallbackFlights = [];
                
                // Handle both array and object responses from Travelpayouts
                if (Array.isArray(cheapData)) {
                  fallbackFlights = cheapData.slice(0, 12).map(c => ({
                    id: `${c.origin}-${c.destination}-${c.depart_date}`,
                    airline: 'Best deal',
                    origin: c.origin,
                    destination: c.destination,
                    departure_time: c.depart_date,
                    arrival_time: c.return_date,
                    price: c.price,
                    source: 'travelpayouts',
                    priority: 2,
                    booking_url: `https://www.aviasales.com/search/${c.origin}${c.destination}${c.depart_date.replace(/-/g,'')}${c.return_date ? c.return_date.replace(/-/g,'') : ''}?utm_source=dejavu-nomad&utm_medium=affiliate&utm_campaign=deals`
                  }));
                } else if (cheapData && typeof cheapData === 'object') {
                  // Handle object response - convert to array
                  const dataArray = Object.values(cheapData).filter(item => 
                    item && typeof item === 'object' && item.origin && item.destination
                  );
                  fallbackFlights = dataArray.slice(0, 12).map(c => ({
                    id: `${c.origin}-${c.destination}-${c.depart_date}`,
                    airline: 'Best deal',
                    origin: c.origin,
                    destination: c.destination,
                    departure_time: c.depart_date,
                    arrival_time: c.return_date,
                    price: c.price,
                    source: 'travelpayouts',
                    priority: 2,
                    booking_url: `https://www.aviasales.com/search/${c.origin}${c.destination}${c.depart_date.replace(/-/g,'')}${c.return_date ? c.return_date.replace(/-/g,'') : ''}?utm_source=dejavu-nomad&utm_medium=affiliate&utm_campaign=deals`
                  }));
                  console.log(`✅ Travelpayouts object converted to ${fallbackFlights.length} flights`);
                }
                
                allFlights = [...fallbackFlights];
                console.log(`✅ Travelpayouts fallback: ${fallbackFlights.length} flights`);
              } catch (e) {
                console.warn('Travelpayouts fallback failed', e);
              }

              // Final fallback to Google Flights if still no flights found
              if (allFlights.length === 0) {
                try {
                  console.log('🔍 Using Google Flights as final fallback...');
                  const googleFlights = await googleFlightsService.searchFlights(originIata, destinationIata, start, end, passengers);
                  allFlights = (googleFlights || []).map(flight => ({
                    ...flight,
                    source: 'google_flights',
                    priority: 3
                  }));
                  console.log(`✅ Google Flights fallback: ${allFlights.length} flights found`);
                } catch (e) {
                  console.warn('Google Flights fallback failed', e);
                }
              }
            }

            // Sort by priority (Duffel first)
            allFlights.sort((a, b) => (a.priority || 999) - (b.priority || 999));
            setDuffelFlights(allFlights);
          }
          
        } catch (duffelError) {
          console.warn('Duffel search failed:', duffelError);
          setDuffelFlights([]);
        } finally {
          setDuffelLoading(false);
        }
      }
      
      // Note: Viator activities are loaded separately in useEffect to avoid date dependency

    } catch (e) {
      setError('Failed to load deals');
      // eslint-disable-next-line no-console
      console.error('Deals load error', e);
    } finally {
      setLoading(false);
    }
  }, [continentSeeds, passengers, destination, computeRange, departDate, returnDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper functions for tab-based loading
  // Loads the fast/reliable providers (Airbnb + Booking.com) first, then streams
  // the slower/bot-prone providers in the background. Pass { refresh: true } to
  // bypass the server cache and fetch live data.
  const loadHotelsForTab = useCallback(async ({ refresh = false, locationOverride = null, radiusMilesOverride = null, centerOverride = null, repositionMap = true } = {}) => {
    if (staysStreamAbortRef.current) {
      staysStreamAbortRef.current.abort();
    }

    const controller = new AbortController();
    staysStreamAbortRef.current = controller;

    setStayGroups({});
    setStaysPendingProviders([]);
    setStaysBackgroundLoading(false);
    setStaysSearchCompleted(false);
    if (repositionMap) {
      // Keep the user's current viewport when the search came from map navigation.
      setStaysMapBounds(null);
      setStaysSearchGeneration((generation) => generation + 1);
    }

    if (!destination && !locationOverride) {
      setDuffelLoading(false);
      if (staysStreamAbortRef.current === controller) {
        staysStreamAbortRef.current = null;
      }
      return;
    }

    const cityName = locationOverride || resolveStaySearchLocation(destination, destinationOptions);
    const radiusMiles = radiusMilesOverride ?? staysRadiusMiles;
    const checkIn = departDate || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    const checkOut = returnDate || new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const streamProviders = (providerIds) =>
      travelService.searchStaysStream(
        {
          location: cityName,
          check_in: checkIn,
          check_out: checkOut,
          guests: passengers,
          radius_miles: radiusMiles,
          latitude: centerOverride?.lat ?? null,
          longitude: centerOverride?.lng ?? null,
          providers: providerIds.join(','),
          refresh,
        },
        {
          signal: controller.signal,
          onStart: ({ providers = [] }) => {
            const ids = providers.map((provider) => provider.id);
            setStaysPendingProviders((prev) => Array.from(new Set([...prev, ...ids])));
          },
          onProvider: ({ provider, listings }) => {
            setStayGroups((prev) => ({
              ...prev,
              [provider]: listings || [],
            }));
            setStaysPendingProviders((prev) => prev.filter((id) => id !== provider));
          },
          onError: (error) => {
            console.error('Stays stream error:', error);
          },
        }
      );

    // Phase 1: fast, reliable providers shown immediately
    try {
      setDuffelLoading(true);
      await streamProviders(STAYS_PRIMARY_PROVIDERS);

      // Map-driven area searches must not rewrite the user's search criteria.
      if (!locationOverride && cityName && cityName !== destination) {
        setDestination(cityName);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Hotel loading error:', error);
        setStayGroups({});
      }
    } finally {
      setDuffelLoading(false);
    }

    // Phase 2: remaining providers streamed in the background
    if (controller.signal.aborted) {
      if (staysStreamAbortRef.current === controller) {
        staysStreamAbortRef.current = null;
      }
      return;
    }

    try {
      setStaysBackgroundLoading(true);
      await streamProviders(STAYS_SECONDARY_PROVIDERS);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Background stays loading error:', error);
      }
    } finally {
      setStaysBackgroundLoading(false);
      setStaysPendingProviders([]);
      if (!controller.signal.aborted) {
        setStaysSearchCompleted(true);
      }
      if (staysStreamAbortRef.current === controller) {
        staysStreamAbortRef.current = null;
      }
    }
  }, [destination, destinationOptions, departDate, returnDate, passengers, staysRadiusMiles]);

  const loadActivitiesForTab = useCallback(async () => {
    if (!destination) return;
    
    try {
      setViatorLoading(true);
      const cityName = destination.length === 3 && destination === destination.toUpperCase() 
        ? getCityName(destination) 
        : destination;
      
      const activities = await travelService.getActivityRecommendations(cityName, 10);
      setViatorActivities(activities);
      
      const generalViatorUrl = travelService.getViatorCityUrl(cityName);
      setViatorCityUrl(generalViatorUrl);
    } catch (error) {
      console.error('Activities loading error:', error);
      setViatorActivities([]);
    } finally {
      setViatorLoading(false);
    }
  }, [destination]);

  // Load all events (default view — no filters)
  const loadAllEvents = useCallback(async () => {
    try {
      const response = await api.get('/event-guides?limit=50');
      setEvents((response.data?.guides || []).map(g => ({
        ...g,
        tags: typeof g.tags === 'string' ? JSON.parse(g.tags) : g.tags || [],
      })));
    } catch (error) {
      console.error('Events loading error:', error);
      setEvents([]);
    }
  }, []);

  // Search events with filters (called only on Search button press)
  const searchEventsForTab = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set('limit', '50');

      if (destination) {
        const resolved = getCityName(destination);
        params.set('search', resolved);
      }
      if (departDate) params.set('date_from', departDate);
      if (returnDate) params.set('date_to', returnDate);

      const response = await api.get(`/event-guides?${params.toString()}`);
      const results = response.data?.guides || [];

      setEvents(results.map(g => ({
        ...g,
        tags: typeof g.tags === 'string' ? JSON.parse(g.tags) : g.tags || [],
      })));
    } catch (error) {
      console.error('Events loading error:', error);
      setEvents([]);
    }
  }, [destination, departDate, returnDate]);

  // Load specific flights between origin and destination
  const loadSpecificFlights = useCallback(async () => {
    if (!origin || !destination) return;
    
    try {
      // Clear any previous results immediately to avoid showing stale data
      setDuffelFlights([]);
      setGroupedDeals({});
      
      setLoading(true);
      setError(null);
      console.log('✈️ Loading specific flights from', origin, 'to', destination);
      
      // Get IATA codes for origin and destination
      const originIata = await travelService.getIATACode(origin);
      const destinationIata = await travelService.getIATACode(destination);
      
      console.log('✈️ IATA codes:', { origin: originIata, destination: destinationIata });
      
      if (originIata && destinationIata) {
        // Search for specific flights using Duffel API
        const { start, end } = computeRange();
        console.log('✈️ Searching for flights with params:', {
          origin: originIata,
          destination: destinationIata,
          departure_date: start,
          return_date: end,
          passengers: passengers,
          cabin_class: 'economy'
        });
        
        // Priority 1: Try Duffel first (always prioritized)
        console.log('✈️ Fetching flights from Duffel (priority)...');
        let duffelFlights = await travelService.getFlightRecommendations({
          origin: originIata,
          destination: destinationIata,
          departure_date: start,
          return_date: end,
          passengers: passengers,
          cabin_class: 'economy'
        });
        
        // Ensure Duffel flights are marked with source
        duffelFlights = (duffelFlights || []).map(flight => ({
          ...flight,
          source: 'duffel',
          priority: 1
        }));
        
        console.log(`✅ Duffel returned ${duffelFlights.length} flights (priority)`);
        
        let allFlights = [...duffelFlights];
        
        // Fallback to Travelpayouts ONLY if Duffel returns no flights
        if (duffelFlights.length === 0) {
          console.log('⚠️ Duffel returned no flights, trying Travelpayouts fallback...');
          try {
            const cheapData = await getCheapPricesFromOrigin(originIata, 'usd', passengers);
            let fallbackFlights = [];
            
            // Handle both array and object responses from Travelpayouts
            if (Array.isArray(cheapData)) {
              fallbackFlights = cheapData.slice(0, 12).map(c => ({
                id: `${c.origin}-${c.destination}-${c.depart_date}`,
                airline: 'Best deal',
                origin: c.origin,
                destination: c.destination,
                departure_time: c.depart_date,
                arrival_time: c.return_date,
                price: c.price,
                source: 'travelpayouts',
                priority: 2,
                booking_url: `https://www.aviasales.com/search/${c.origin}${c.destination}${c.depart_date.replace(/-/g,'')}${c.return_date ? c.return_date.replace(/-/g,'') : ''}?utm_source=dejavu-nomad&utm_medium=affiliate&utm_campaign=deals`
              }));
            } else if (cheapData && typeof cheapData === 'object') {
              // Handle object response - convert to array
              const dataArray = Object.values(cheapData).filter(item => 
                item && typeof item === 'object' && item.origin && item.destination
              );
              fallbackFlights = dataArray.slice(0, 12).map(c => ({
                id: `${c.origin}-${c.destination}-${c.depart_date}`,
                airline: 'Best deal',
                origin: c.origin,
                destination: c.destination,
                departure_time: c.depart_date,
                arrival_time: c.return_date,
                price: c.price,
                source: 'travelpayouts',
                priority: 2,
                booking_url: `https://www.aviasales.com/search/${c.origin}${c.destination}${c.depart_date.replace(/-/g,'')}${c.return_date ? c.return_date.replace(/-/g,'') : ''}?utm_source=dejavu-nomad&utm_medium=affiliate&utm_campaign=deals`
              }));
              console.log(`✅ Travelpayouts object converted to ${fallbackFlights.length} flights`);
            }
            
            allFlights = [...fallbackFlights];
            console.log(`✅ Travelpayouts fallback: ${fallbackFlights.length} flights`);
          } catch (e) {
            console.warn('Travelpayouts fallback failed', e);
          }

          // Final fallback to Google Flights if still no flights found
          if (allFlights.length === 0) {
            try {
              console.log('🔍 Using Google Flights as final fallback...');
              const googleFlights = await googleFlightsService.searchFlights(originIata, destinationIata, start, end, passengers);
              allFlights = (googleFlights || []).map(flight => ({
                ...flight,
                source: 'google_flights',
                priority: 3
              }));
              console.log(`✅ Google Flights fallback: ${allFlights.length} flights found`);
            } catch (e) {
              console.warn('Google Flights fallback failed', e);
            }
          }
        }
        
        // Sort by priority (Duffel first)
        allFlights.sort((a, b) => (a.priority || 999) - (b.priority || 999));
        console.log('✈️ Specific flights found:', allFlights.length);
        
        // Set specific flights instead of continent-based deals
        setDuffelFlights(allFlights);
        
        // Clear continent-based deals when showing specific flights
        setGroupedDeals({});
      } else {
        console.log('✈️ No IATA codes found, falling back to general deals');
        await loadDeals(origin);
      }
    } catch (error) {
      console.error('Specific flight search error:', error);
      setError('Failed to load specific flights');
      // Fallback to general deals
      await loadDeals(origin);
    } finally {
      setLoading(false);
    }
  }, [origin, destination, passengers, computeRange, loadDeals]);

  // Debounced search function that triggers when input fields change
  // Removed triggerSearch - users must click Search button instead of auto-triggering
  
  // Initial flights loading will be handled after loadDeals is defined

  // Restore search when returning from flight details page
  useEffect(() => {
    // Check if we have URL params indicating a return from flight details
    if (urlOrigin && urlDestination && !shouldRestoreSearch) {
      console.log('🔄 Restoring search from URL params:', { urlOrigin, urlDestination, urlDepartDate, urlReturnDate });
      setShouldRestoreSearch(true);
      // Trigger the search with restored params
      setTimeout(() => {
        loadSpecificFlights();
      }, 100);
    }
  }, [urlOrigin, urlDestination, urlDepartDate, urlReturnDate, shouldRestoreSearch, loadSpecificFlights]);

  // Note: Removed auto-trigger on field changes - users must click Search button
  
  // Note: Tab changes now handled directly in Tabs onChange={(e, newValue) => setActiveTab(newValue)}
  const [duffelFlights, setDuffelFlights] = useState([]);
  const [stayGroups, setStayGroups] = useState({});
  const [staysPendingProviders, setStaysPendingProviders] = useState([]);
  const [staysBackgroundLoading, setStaysBackgroundLoading] = useState(false);
  // True only once a full search has finished, so the "search directly" /
  // "no results" sections never flash while providers are still loading.
  const [staysSearchCompleted, setStaysSearchCompleted] = useState(false);
  const staysStreamAbortRef = useRef(null);

  const rentalStayListings = useMemo(
    () => ['googlerentals', 'liteapirentals', 'airbnb', 'vrbo', 'redweek'].flatMap((provider) =>
      (stayGroups[provider] || []).filter((listing) => !listing.isDeepLink)
    ),
    [stayGroups]
  );
  const hotelStayListings = useMemo(
    () => ['googlehotels', 'liteapi', 'impt', 'booking', 'orbitz', 'hotels'].flatMap((provider) =>
      (stayGroups[provider] || []).filter((listing) => !listing.isDeepLink)
    ),
    [stayGroups]
  );
  const stayProviderFallbacks = useMemo(
    () => ['googlehotels', 'googlerentals', 'liteapi', 'liteapirentals', 'impt', 'airbnb', 'vrbo', 'redweek', 'booking', 'orbitz', 'hotels'].flatMap((provider) =>
      (stayGroups[provider] || []).filter((listing) => listing.isDeepLink)
    ),
    [stayGroups]
  );
  const hasStaySearchResults = rentalStayListings.length + hotelStayListings.length > 0;
  const allStayListings = useMemo(
    () => [...rentalStayListings, ...hotelStayListings],
    [rentalStayListings, hotelStayListings]
  );
  const mappableStayCount = useMemo(
    () =>
      allStayListings.filter(
        (listing) =>
          Number.isFinite(Number(listing.latitude)) && Number.isFinite(Number(listing.longitude))
      ).length,
    [allStayListings]
  );
  const [staysViewMode, setStaysViewMode] = useState('map');
  const [staysMapBounds, setStaysMapBounds] = useState(null);
  const [staysSearchGeneration, setStaysSearchGeneration] = useState(0);
  const showStaysMap = staysViewMode === 'map' && Boolean(destination) && staysSearchGeneration > 0;
  const showStaysList = staysViewMode === 'list' || mappableStayCount === 0;
  const mapVisibleStayListings = useMemo(() => {
    if (!staysMapBounds) {
      return [];
    }
    return allStayListings.filter((listing) => isListingInMapBounds(listing, staysMapBounds));
  }, [allStayListings, staysMapBounds]);
  const mapVisibleRentalListings = useMemo(
    () => mapVisibleStayListings.filter((listing) => listing.type === 'rental'),
    [mapVisibleStayListings]
  );
  const mapVisibleHotelListings = useMemo(
    () => mapVisibleStayListings.filter((listing) => listing.type !== 'rental'),
    [mapVisibleStayListings]
  );
  const handleStaysViewportChange = useCallback((bounds) => {
    setStaysMapBounds(bounds);
  }, []);
  // Re-search stays for the area the user panned/zoomed to. The destination and
  // radius fields stay untouched — they're only the initial entry point.
  const handleStaysSearchArea = useCallback(async ({ lat, lng, radiusMiles }) => {
    let areaName = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    try {
      if (window.google?.maps?.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        const { results } = await geocoder.geocode({ location: { lat, lng } });
        const locality =
          results?.find((result) => result.types.includes('locality')) ||
          results?.find((result) => result.types.includes('administrative_area_level_2')) ||
          results?.find((result) => result.types.includes('administrative_area_level_1')) ||
          results?.[0];
        if (locality?.formatted_address) {
          areaName = locality.formatted_address;
        }
      }
    } catch (error) {
      console.warn('Reverse geocode for map area failed:', error);
    }

    const clampedRadius = Math.max(1, Math.min(100, Math.round(radiusMiles)));
    loadHotelsForTab({
      locationOverride: areaName,
      radiusMilesOverride: clampedRadius,
      centerOverride: { lat, lng },
      repositionMap: false,
    });
  }, [loadHotelsForTab]);
  const stayDestinationDisplay = useMemo(() => {
    if (!destination) {
      return '';
    }
    return resolveStaySearchLocation(destination, destinationOptions);
  }, [destination, destinationOptions]);
  
  // Sort and filter flights (not used after flights tab simplification)
  // eslint-disable-next-line no-unused-vars
  const sortedFlights = useMemo(() => {
    if (!duffelFlights || duffelFlights.length === 0) return [];
    
    const flightsToSort = [...duffelFlights];
    
    // Helper function to parse duration string (e.g., "5h 30m" -> 330 minutes)
    const parseDuration = (duration) => {
      if (!duration) return 0;
      const hours = duration.match(/(\d+)h/);
      const minutes = duration.match(/(\d+)m/);
      return (hours ? parseInt(hours[1]) * 60 : 0) + (minutes ? parseInt(minutes[1]) : 0);
    };
    
    switch (sortBy) {
      case 'price':
        return flightsToSort.sort((a, b) => {
          const priceA = parseFloat(a.price?.replace(/[^0-9.-]+/g, '') || a.total_amount || 0);
          const priceB = parseFloat(b.price?.replace(/[^0-9.-]+/g, '') || b.total_amount || 0);
          return priceA - priceB;
        });
      case 'duration':
        return flightsToSort.sort((a, b) => {
          const durationA = a.duration ? parseDuration(a.duration) : Infinity;
          const durationB = b.duration ? parseDuration(b.duration) : Infinity;
          return durationA - durationB;
        });
      case 'stops':
        return flightsToSort.sort((a, b) => {
          const stopsA = a.stops !== undefined ? a.stops : Infinity;
          const stopsB = b.stops !== undefined ? b.stops : Infinity;
          return stopsA - stopsB;
        });
      case 'airline':
        return flightsToSort.sort((a, b) => {
          const airlineA = (a.airline || '').toLowerCase();
          const airlineB = (b.airline || '').toLowerCase();
          return airlineA.localeCompare(airlineB);
        });
      default:
        return flightsToSort;
    }
  }, [duffelFlights, sortBy]);
  
  // Limit displayed flights
  // const displayedFlights = showAllFlights ? sortedFlights : sortedFlights.slice(0, 30); // Not used after flights tab simplification
  const [viatorActivities, setViatorActivities] = useState([]);
  const [duffelLoading, setDuffelLoading] = useState(false);
  const [viatorLoading, setViatorLoading] = useState(false);
  const [matrix, setMatrix] = useState([]);
  const [matrixLoading, setMatrixLoading] = useState(false);
  
  // Cached default flights (not used after flights tab simplification)
  // Default flights cache removed - flights fetched on-demand
  const imageCacheRef = useRef(new Map());
  // const apiCallCacheRef = useRef(new Map()); // Disabled to avoid Google API billing issues
  const prefetchingRef = useRef(false);
  const popularStayCities = ['Las Vegas','Rome','Paris','London','New York City','Washington DC','Cancun','Florence','Barcelona','Oahu'];

  /* Not used after flights tab simplification
  const normalizeContinents = useCallback((grouped) => {
    const mapCodeToName = {
      AF: 'Africa',
      AS: 'Asia',
      EU: 'Europe',
      NA: 'North America',
      SA: 'South America',
      OC: 'Oceania'
    };
    const buckets = {
      'Africa': [],
      'Asia': [],
      'Europe': [],
      'North America': [],
      'South America': [],
      'Oceania': [],
      'Other': []
    };
    Object.entries(grouped || {}).forEach(([code, deals]) => {
      const label = mapCodeToName[code] || code || 'Other';
      if (buckets[label]) buckets[label].push(...deals);
      else buckets['Other'].push(...deals);
    });
    // Define the 5 requested rows; include Oceania only if present; drop Other by default
    const order = ['Africa','Asia','Europe','North America','South America'];
    const rows = order
      .map((label) => [label, buckets[label] || []])
      .filter(([, arr]) => arr && arr.length > 0);
    if ((buckets['Oceania'] || []).length > 0) rows.push(['Oceania', buckets['Oceania']]);
    return rows;
  }, []);
  */

  // image cache access helper (kept for clarity)
  // eslint-disable-next-line no-unused-vars
  const getImageForDestination = (name) => (name ? imageCacheRef.current.get(name) || '' : '');

  // Get photo URL for a place - DISABLED to avoid Google API billing issues
  // const getPhotoUrl = useCallback((place) => {
  //   if (place.photos && place.photos.length > 0) {
  //     const photo = place.photos[0];
  //     // Handle both old and new API photo formats
  //     if (typeof photo.getUrl === 'function') {
  //       return photo.getUrl({ maxWidth: 360, maxHeight: 240 });
  //     } else if (photo.name) {
  //       // New API format - construct URL
  //       return `https://places.googleapis.com/v1/${photo.name}/media?maxWidthPx=360&maxHeightPx=240&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
  //     }
  //   }
  //   return null;
  // }, []);

  // const fetchGoogleImage = useCallback(async (placeName) => { // Disabled to avoid Google API billing issues
  //   // Use fallback images instead of Google Places API to avoid billing issues
  //   const fallbackImage = getFallbackImage(placeName);
  //   console.log(`📸 Using fallback image for ${placeName}:`, fallbackImage);
  //   return fallbackImage;
  // }, []);


  // Optimized batch fetch images - process destinations in smaller batches with delays
  const fetchBatchGoogleImages = useCallback(async (destNames) => {
    console.log(`🔍 fetchBatchGoogleImages called for:`, destNames);
    
    // Use fallback images instead of Google Places API to avoid billing issues
    const results = destNames.map(destName => {
      const fallbackImage = getFallbackImage(destName);
      return { name: destName, url: fallbackImage };
    });
    
    console.log(`📸 Using fallback images for:`, destNames);
      return results;
  }, []);

      const prefetchImages = useCallback(async (destNames) => {
        // Prevent multiple simultaneous prefetch operations
        if (prefetchingRef.current) {
          console.log('⏭️ Prefetch already in progress, skipping...');
          return;
        }
        
        prefetchingRef.current = true;
        console.log('🖼️ prefetchImages called with:', destNames);
        const updates = {};
        
        try {
          // Filter out already cached destinations
          const uncachedDestinations = destNames.filter(name => !imageCacheRef.current.has(name));
          console.log('🔍 Uncached destinations:', uncachedDestinations);
          
          if (uncachedDestinations.length === 0) {
            console.log('⏭️ All images already cached');
            return;
          }
          
          // Try batch Google Places search for multiple destinations at once
          console.log('🔍 Starting batch image fetch for:', uncachedDestinations);
          const batchResults = await fetchBatchGoogleImages(uncachedDestinations);
          console.log('📸 Batch results:', batchResults);
          
          // Process results
          batchResults.forEach(({ name, url }) => {
            if (url) {
              imageCacheRef.current.set(name, url);
              updates[name] = url;
              console.log(`✅ Image cached for ${name}:`, url);
            } else {
              console.warn(`❌ No image found for ${name}`);
            }
          });
          
          console.log('🖼️ Final image updates:', updates);
          if (Object.keys(updates).length > 0) {
            setImageMap((prev) => ({ ...prev, ...updates }));
          }
        } finally {
          prefetchingRef.current = false;
        }
      }, [fetchBatchGoogleImages]);

  // kept placeholder in case we restore external links later

  const formatDatePretty = (value) => {
    if (!value) return '';
    try {
      // If it looks like YYYY-MM-DD, show date only
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const d = new Date(value + 'T00:00:00');
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      }
      // ISO date-time like 2025-10-28T05:30:00Z
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
      }
      return value;
    } catch {
      return value;
    }
  };

  // Load flights on page load (Flights tab is default)
      // Removed auto-load on mount - users must click Search button

      // Removed - now handled by initial page load

      // Removed - now handled by tab change


      // Removed auto-load events on destination change - users must click Search button

      // Removed - now handled by tab change

      // Load flights only when Flights tab is active and required fields are set
      // Removed - now handled by tab change

      // Removed - now handled by tab change

      // Load cached images into state on mount (only once)
      useEffect(() => {
        const cachedImages = {};
        for (const [name, url] of imageCacheRef.current.entries()) {
          cachedImages[name] = url;
        }
        if (Object.keys(cachedImages).length > 0) {
          // Loading cached images into state
          setImageMap(prev => ({ ...prev, ...cachedImages }));
        }
      }, []);

      // Fallback images for when Google Places API fails
      const getFallbackImage = (destination) => {
        const fallbackImages = {
          'Cape Town': 'https://picsum.photos/400/300?random=10',
          'Nairobi': 'https://picsum.photos/400/300?random=11',
          'Marrakesh': 'https://picsum.photos/400/300?random=12',
          'Cairo': 'https://picsum.photos/400/300?random=13',
          'Tokyo': 'https://picsum.photos/400/300?random=14',
          'Bangkok': 'https://picsum.photos/400/300?random=15',
          'Singapore': 'https://picsum.photos/400/300?random=16',
          'Bali': 'https://picsum.photos/400/300?random=17',
          'Paris': 'https://picsum.photos/400/300?random=18',
          'London': 'https://picsum.photos/400/300?random=19',
          'Rome': 'https://picsum.photos/400/300?random=20',
          'Barcelona': 'https://picsum.photos/400/300?random=21',
          'New York': 'https://picsum.photos/400/300?random=22',
          'Los Angeles': 'https://picsum.photos/400/300?random=23',
          'Toronto': 'https://picsum.photos/400/300?random=24',
          'Cancun': 'https://picsum.photos/400/300?random=25',
          'Rio de Janeiro': 'https://picsum.photos/400/300?random=26',
          'Buenos Aires': 'https://picsum.photos/400/300?random=27',
          'Lima': 'https://picsum.photos/400/300?random=28',
          'Santiago': 'https://picsum.photos/400/300?random=29',
          'Sydney': 'https://picsum.photos/400/300?random=30',
          'Auckland': 'https://picsum.photos/400/300?random=31',
          'Melbourne': 'https://picsum.photos/400/300?random=32',
          'Fiji': 'https://picsum.photos/400/300?random=33',
          // Add more specific mappings for common destinations
          'Las Vegas': 'https://picsum.photos/400/300?random=34',
          'New York City': 'https://picsum.photos/400/300?random=35',
          'Washington DC': 'https://picsum.photos/400/300?random=36',
          'Florence': 'https://picsum.photos/400/300?random=37',
          'Oahu': 'https://picsum.photos/400/300?random=38'
        };
        return fallbackImages[destination] || 'https://picsum.photos/400/300?random=99';
      };

      // Initialize Google Places Autocomplete for destination disambiguation
      useEffect(() => {
        const initAutocomplete = async () => {
          try {
            await loadGoogleMaps();
            // Use legacy AutocompleteService for now (new API method not implemented yet)
            if (window.google?.maps?.places?.AutocompleteService) {
              setAutocompleteService(new window.google.maps.places.AutocompleteService());
            }
          } catch (error) {
            console.error('Failed to load Google Maps for autocomplete:', error);
          }
        };
        initAutocomplete();
      }, []); // Empty dependency array - only run once

      // Handle image loading when groupedDeals changes (only once per change)
      useEffect(() => {
        if (!groupedDeals || Object.keys(groupedDeals).length === 0) return;
        
        const names = Object.values(groupedDeals)
          .flat()
          .map((x) => x.destinationName)
          .filter(Boolean);
        
        console.log('🖼️ Image prefetch triggered for destinations:', names);
        
        if (names.length > 0 && !prefetchingRef.current) {
          // Starting image prefetch
          // Call prefetchImages directly without dependency
          prefetchImages(names);
        }
      }, [groupedDeals]); // eslint-disable-line react-hooks/exhaustive-deps

      // Handle image loading for hotels when Stays tab is active
      useEffect(() => {
        if (activeTab === 4 && !prefetchingRef.current) {
          console.log('🖼️ Image prefetch triggered for hotels:', popularStayCities);
          prefetchImages(popularStayCities);
        }
      }, [activeTab, prefetchImages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load custom package based on search criteria (origin, destination, dates)
  const loadCustomPackage = useCallback(async () => {
    try {
      setLoading(true);
      console.log(`🏗️ Building custom package: ${origin} → ${destination} (${departDate} to ${returnDate})`);
      
      // Convert destination to city name if it's an IATA code (handle any case)
      const destinationCity = destination.length === 3 
        ? getCityName(destination.toUpperCase()) 
        : destination;
      
      console.log(`🔄 Converting destination: ${destination} → ${destinationCity}`);
      
      // Build a custom package with flights, hotels, and activities
      const customPackage = {
        id: `custom_${Date.now()}`,
        destination: destinationCity,
        type: 'custom',
        duration: returnDate && departDate ? Math.ceil((new Date(returnDate) - new Date(departDate)) / (1000 * 60 * 60 * 24)) : 7,
        description: `Explore ${destinationCity} with this custom vacation package. Book flights, hotels, and activities separately to create your perfect trip.`,
        included: [
          'Round-trip flights',
          'Hotel accommodation',
          'Activities & experiences',
          'Travel assistance'
        ],
        pricing: {
          total: 0, // Will be calculated from actual results
          savings: 0
        },
        origin: origin,
        departure_date: departDate,
        return_date: returnDate,
        passengers: passengers,
        imageUrl: getDestinationImage(destinationCity)
      };
      
      // Set this as the only package to display
      setVacationPackages([customPackage]);
      
      // Load activities directly using Viator API (not dependent on state)
      console.log(`🎭 Loading Viator activities for ${destinationCity}...`);
      try {
        setViatorLoading(true);
        
        // Call Viator API directly instead of going through travelService
        const viatorResponse = await viatorService.getRecommendations(destinationCity, 10);
        const activities = viatorResponse.activities || [];
        
        console.log(`✅ Got ${activities.length} Viator activities for ${destinationCity}:`, 
          activities.map(a => a.title || a.name).join(', '));
        
        // Validate that activities are for the correct destination
        // Filter out any Paris/London/Rome activities if they somehow got through
        const validActivities = activities.filter(activity => {
          const title = (activity.title || activity.name || '').toLowerCase();
          const isParis = title.includes('eiffel') || title.includes('louvre') || title.includes('seine');
          const isLondon = title.includes('london eye') || title.includes('tower of london');
          const isRome = title.includes('colosseum');
          
          if (isParis || isLondon || isRome) {
            console.warn(`⚠️ Filtering out invalid activity: ${activity.title || activity.name}`);
            return false;
          }
          return true;
        });
        
        console.log(`✅ After filtering: ${validActivities.length} valid activities for ${destinationCity}`);
        setViatorActivities(validActivities);
        
        const generalViatorUrl = travelService.getViatorCityUrl(destinationCity);
        setViatorCityUrl(generalViatorUrl);
      } catch (error) {
        console.error('❌ Viator API error:', error);
        console.error('Error details:', error.response?.data || error.message);
        
        // Check if it's a rate limit error
        if (error.response?.status === 429) {
          console.error('🚫 Viator API rate limit exceeded. Please wait before trying again.');
        }
        
        // Don't use fallback - just show empty
        setViatorActivities([]);
      } finally {
        setViatorLoading(false);
      }
      
      // Load hotels
      await loadHotelsForTab();
      
      console.log(`✅ Custom package built for ${destinationCity}`);
    } catch (error) {
      console.error('Failed to build custom package:', error);
      setVacationPackages([]);
    } finally {
      setLoading(false);
    }
  }, [origin, destination, departDate, returnDate, passengers, loadHotelsForTab]);

  // Load vacation packages based on type and destination
  const loadVacationPackages = useCallback(async (type, destination) => {
    try {
      setLoading(true);
      
      // If we have a specific destination, use the package builder to create custom packages
      if (destination) {
        console.log(`🏗️ Building custom packages for ${destination} (${type})`);
        
        // Set default dates
        const { start, end } = computeRange();
        setDepartDate(start);
        setReturnDate(end);
        
        // Load packages using package builder
        try {
          const packageResult = await packageBuilderService.buildPackage({
            origin: origin || 'NYC',
            destination: destination,
            departure_date: start,
            return_date: end,
            passengers: passengers,
            cabin_class: 'economy',
            check_in_date: start,
            check_out_date: end,
            rooms: Math.ceil(passengers / 2), // Assume 2 people per room
            guests: passengers,
            limit: 10
          });
          
          if (packageResult.success && packageResult.packages) {
            // Convert package builder results to vacation package format
            const vacationPackages = packageResult.packages.map(pkg => ({
              id: pkg.id,
              destination: destination,
              type: type || 'city',
              duration: pkg.duration || 7,
              description: `Custom ${destination} package with flights and hotels`,
              included: [
                'Round-trip flights',
                pkg.hotel ? 'Hotel accommodation' : 'Flight only',
                'Travel assistance'
              ],
              pricing: {
                total: pkg.total_price?.amount || 0,
                savings: pkg.savings?.amount || 0
              },
              origin: pkg.flight?.origin || origin || 'NYC',
              departure_date: start,
              return_date: end,
              passengers: passengers,
              packageData: pkg // Store full package data for details
            }));
            
            setVacationPackages(vacationPackages);
            console.log(`✅ Built ${vacationPackages.length} custom packages for ${destination}`);
            return;
          }
        } catch (packageError) {
          console.warn('Package builder failed, falling back to static packages:', packageError);
        }
      }
      
      // Fallback to static packages - use origin if available, otherwise default to LAX
      const originCode = origin || 'LAX';
      const packages = await packageService.getPopularPackages(originCode);
      
      // Filter packages by type and destination
      let filteredPackages = packages;
      if (type) {
        filteredPackages = packages.filter(pkg => pkg.type === type);
        console.log(`🔍 Filtered packages by type "${type}":`, filteredPackages.length);
      }
      if (destination) {
        filteredPackages = filteredPackages.filter(pkg => 
          pkg.destination.toLowerCase().includes(destination.toLowerCase())
        );
        console.log(`🔍 Filtered packages by destination "${destination}":`, filteredPackages.length);
      }
      
      // Update passenger count for all packages
      const updatedPackages = filteredPackages.map(pkg => ({
        ...pkg,
        passengers: passengers
      }));
      
      console.log(`✅ Loaded ${updatedPackages.length} vacation packages from ${originCode} for ${passengers} passengers`);
      setVacationPackages(updatedPackages);
    } catch (error) {
      console.error('Failed to load vacation packages:', error);
      setVacationPackages([]);
    } finally {
      setLoading(false);
    }
  }, [computeRange, origin, passengers]);

  // Partner catalog visitors should stay on scoped packages, not the marketplace tab.
  useEffect(() => {
    const catalogPath = getPartnerCatalogPath();
    if (!catalogPath || location.pathname !== '/deals') return;
    const tab = new URLSearchParams(location.search).get('tab');
    const isPackagesTab = !tab || tab === 'packages' || tab === '6';
    if (isPackagesTab) {
      navigate(catalogPath, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  // Handle query parameters for vacation packages and tab selection
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const packageDestination = searchParams.get('destination');
    const tab = searchParams.get('tab');
    const agent = searchParams.get('agent');
    const sub = searchParams.get('sub');

    if (agent) {
      setSelectedPackageAgentId(agent);
      setPackagesSubTab('agent');
    } else if (sub === 'private' && privateTravelAvailable) {
      setPackagesSubTab('private');
    } else if (sub === 'private' && !privateTravelAvailable) {
      setPackagesSubTab('agent');
    } else if (sub === 'build') {
      setPackagesSubTab('build');
    } else if (sub === 'agent') {
      setPackagesSubTab('agent');
    }
    
    // Handle tab parameter - support both string names and numeric values
    if (tab === 'destinations' || tab === '0') {
      setActiveTab(0); // Destinations tab
    } else if (tab === 'road-trips' || tab === '1') {
      setActiveTab(1); // Road Trips tab
    } else if (tab === 'flights' || tab === '2') {
      setActiveTab(2); // Flights tab
    } else if (tab === 'stays' || tab === '3') {
      setActiveTab(3); // Stays tab
    } else if (tab === 'things-to-do' || tab === '4') {
      setActiveTab(4); // Things to do tab
    } else if (tab === 'events' || tab === '5') {
      setActiveTab(5); // Events tab
    } else if (tab === 'packages' || tab === '6') {
      setActiveTab(6); // Packages tab
      loadVacationPackages(null, packageDestination);
    } else if (!tab) {
      setActiveTab(6);
      loadVacationPackages(null, packageDestination);
    }
  }, [location.search, loadVacationPackages, privateTravelAvailable]);

  useEffect(() => {
    if (!privateTravelAvailable && packagesSubTab === 'private') {
      setPackagesSubTab('agent');
    }
  }, [privateTravelAvailable, packagesSubTab]);

  // Load partner/agent packages (excludes private journey listings)
  const loadPartnerPackages = useCallback(async (searchQuery = '', destinationFilter = '', isInitialLoad = false) => {
    try {
      setPartnerPackagesLoading(true);
      
      let url = '/travel-packages/search?limit=48';
      if (searchQuery) {
        url += `&q=${encodeURIComponent(searchQuery)}`;
      }
      if (destinationFilter) {
        url += `&destination=${encodeURIComponent(destinationFilter)}`;
      }
      
      const response = await api.get(url);
      
      if (response.data.success && response.data.packages) {
        setPartnerPackages(response.data.packages);
        
        // On initial load, switch to 'build' tab if no agent packages
        if (isInitialLoad && response.data.packages.length === 0) {
          setPackagesSubTab('build');
        } else if (isInitialLoad && response.data.packages.length > 0) {
          setPackagesSubTab('agent');
        }
      } else {
        setPartnerPackages([]);
        if (isInitialLoad) {
          setPackagesSubTab('build');
        }
      }
    } catch (error) {
      console.error('Failed to load partner packages:', error);
      setPartnerPackages([]);
    } finally {
      setPartnerPackagesLoading(false);
    }
  }, []);

  const loadPrivatePackages = useCallback(async () => {
    try {
      setPrivatePackagesLoading(true);
      const response = await api.get('/travel-packages/search?private_journey=1&limit=48');
      setPrivatePackages(response.data?.success ? (response.data.packages || []) : []);
    } catch (error) {
      console.error('Failed to load private packages:', error);
      setPrivatePackages([]);
    } finally {
      setPrivatePackagesLoading(false);
    }
  }, []);

  const openPrivateJourneyRequest = useCallback(async () => {
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent('/deals?tab=packages&sub=private')}`);
      return;
    }
    setPrivateRequestBusy(true);
    try {
      const response = await api.get('/package-booking/private-request-target');
      if (!response.data?.available) return;
      setPrivateRequestTarget({
        partnerId: response.data.partnerId,
        partnerName: response.data.partnerName,
      });
      setPrivateRequestOpen(true);
    } catch (error) {
      console.error('Failed to open private request:', error);
    } finally {
      setPrivateRequestBusy(false);
    }
  }, [navigate, user]);

  // Load destinations when destinations tab is active
  useEffect(() => {
    if (activeTab === 0 && destinationsData.length === 0) {
      const fetchDestinations = async () => {
        setDestinationsLoading(true);
        try {
          const [destRes, contRes] = await Promise.all([
            api.get('/destinations?limit=200'),
            api.get('/destinations/continents')
          ]);
          setDestinationsData(destRes.data?.destinations || destRes.data || []);
          setDestinationsContinents(contRes.data || []);
        } catch (err) {
          console.error('Failed to load destinations:', err);
        } finally {
          setDestinationsLoading(false);
        }
      };
      fetchDestinations();
    }
  }, [activeTab, destinationsData.length]);

  // Load partner packages when packages tab is active
  useEffect(() => {
    if (activeTab === 6) {
      if (!packagesInitialLoad) {
        setPackagesInitialLoad(true);
        loadPartnerPackages('', '', true);
      } else if (packagesSubTab === 'agent') {
        loadPartnerPackages();
      } else if (packagesSubTab === 'private') {
        loadPrivatePackages();
      }
    }
  }, [activeTab, packagesSubTab, loadPartnerPackages, loadPrivatePackages, packagesInitialLoad]);

  // Load all events when Events tab is first opened (default view)
  useEffect(() => {
    if (activeTab === 5) {
      loadAllEvents();
    }
  }, [activeTab, loadAllEvents]);

  const handleSearchMatrix = async () => {
    try {
      setMatrixLoading(true);
      
      // Handle search based on active tab
        if (activeTab === 2) {
        // Flights tab - Navigate to dedicated search results page
        if (origin && destination) {
          // origin and destination are already comma-separated IATA codes from the multi-select Autocomplete
          // No need to convert them again
          console.log(`🔍 Multi-airport search: ${origin} → ${destination}`);
          
          // Navigate to the new Duffel-style search page
          const searchParams = new URLSearchParams({
            origin: origin,
            destination: destination,
            departDate,
            returnDate: returnDate || '',
            passengers: passengers.toString(),
            tripType
          });
          navigate(`/flights/search?${searchParams.toString()}`);
          return;
        } else if (origin) {
          // Load general deals from origin (single airport)
          const { convertToIATA } = await import('../utils/airportDatabase');
          const originIATA = convertToIATA(origin);
          await loadDeals(originIATA);
        }
      } else if (activeTab === 3) {
        // Stays/Hotels tab
        if (destination) {
          await loadHotelsForTab();
        }
      } else if (activeTab === 4) {
        // Things to do tab
        if (destination) {
          await loadActivitiesForTab();
        }
      } else if (activeTab === 5) {
        // Events tab — search with filters when Search button is pressed
        if (destination || departDate || returnDate) {
          await searchEventsForTab();
        } else {
          await loadAllEvents();
        }
      } else if (activeTab === 6) {
        // Packages tab
        if (origin && destination) {
          // Build custom package based on search criteria
          await loadCustomPackage();
        } else {
          // Show default packages if no search criteria
        await loadVacationPackages(null, null);
        }
      }
      
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Week matrix fetch failed', e);
      setMatrix([]);
    } finally {
      setMatrixLoading(false);
    }
  };

  // Handle autocomplete for airports (origin field) - Uses airport database
  const handleOriginAutocomplete = useCallback(async (inputValue) => {
    if (!inputValue || inputValue.length < 3) {
        setOriginOptions([]);
    return;
    }

    // Use the comprehensive airport database
    const { searchAirports } = await import('../utils/airportDatabase');
    const results = searchAirports(inputValue);

    if (results.length > 0) {
      setOriginOptions(results);
      return;
    }

    // If no matches in our database and Google autocomplete is available, use it as fallback
    if (autocompleteService) {
      try {
        const request = {
          input: inputValue,
          types: ['(cities)'],
          language: 'en',
          ...(window.google?.maps?.places?.AutocompleteSessionToken
            ? { sessionToken: new window.google.maps.places.AutocompleteSessionToken() }
            : {})
        };

        autocompleteService.getPlacePredictions(request, (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            const options = predictions.map((prediction) => ({
              label: prediction.description,
              value: prediction.place_id
            }));
            setOriginOptions(options);
          } else {
            setOriginOptions([]);
          }
        });
      } catch (error) {
        console.error('Error fetching origin predictions:', error);
        setOriginOptions([]);
      }
    } else {
      setOriginOptions([]);
    }
  }, [autocompleteService]);

  // Handle autocomplete for cities (destination field) - ENABLED for location disambiguation
  const handleDestinationAutocomplete = useCallback(async (inputValue, tab = activeTab) => {
    if (!inputValue || inputValue.length < 3) {
      setDestinationOptions([]);
      return;
    }

    const useAirportSearch = tab === 2 || tab === 6;

    if (useAirportSearch) {
      const { searchAirports } = await import('../utils/airportDatabase');
      const results = searchAirports(inputValue);

      if (results.length > 0) {
        setDestinationOptions(results);
        return;
      }
    }

    // Stays, Things to do, and other non-flight tabs use cities/places — not IATA airports
    if (autocompleteService) {
      try {
        const { searchAirports } = await import('../utils/airportDatabase');
        const cityFallback = () => searchAirports(inputValue).map((airport) => ({
          label: `${airport.city}, ${airport.country}`,
          value: airport.city,
          city: airport.city,
          country: airport.country,
        }));

        const request = {
          input: inputValue,
          types: ['(cities)'],
          language: 'en',
          ...(window.google?.maps?.places?.AutocompleteSessionToken
            ? { sessionToken: new window.google.maps.places.AutocompleteSessionToken() }
            : {})
        };

        autocompleteService.getPlacePredictions(request, (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            const options = predictions.map(prediction => ({
              label: prediction.description,
              value: prediction.description,
              placeId: prediction.place_id
            }));
            setDestinationOptions(options);
            return;
          }

          if (!useAirportSearch) {
            setDestinationOptions(cityFallback());
            return;
          }

          setDestinationOptions([]);
        });
      } catch (error) {
        console.error('Destination autocomplete error:', error);
        setDestinationOptions([]);
      }
    } else if (!useAirportSearch) {
      const { searchAirports } = await import('../utils/airportDatabase');
      const airportResults = searchAirports(inputValue).map((airport) => ({
        label: `${airport.city}, ${airport.country}`,
        value: airport.city,
        city: airport.city,
        country: airport.country,
      }));
      setDestinationOptions(airportResults);
    } else {
      setDestinationOptions([]);
    }
  }, [autocompleteService, activeTab]);

  // Extract IATA code from airport description - DISABLED to avoid Google API billing issues
  // const extractIATACode = (description) => {
  //   const match = description.match(/\(([A-Z]{3})\)/);
  //   return match ? match[1] : null;
  // };

  // Extract city name from description - DISABLED to avoid Google API billing issues
  // const extractCityName = (description) => {
  //   return description.split(',')[0].trim();
  // };

  return (
    <>
      <SEOHead 
        title="Travel Deals 2026 | Cheap Vacation Packages & Flight Deals - Déjà Vu Nomad"
        description="Find the best travel deals for 2026. Compare cheap vacation packages, flight deals, all-inclusive resorts, and last-minute getaways. Book affordable trips to top destinations worldwide."
        keywords="travel deals, vacation deals, cheap vacations, vacation packages, flight deals, all inclusive deals, last minute travel deals, cheap flights, holiday packages, travel discounts, affordable vacations, budget travel deals"
        canonical="https://dejavunomad.com/deals"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Travel Deals & Vacation Packages",
          "description": "Browse the best travel deals, vacation packages, and flight discounts",
          "url": "https://dejavunomad.com/deals",
          "isPartOf": {
            "@type": "WebSite",
            "name": "Déjà Vu Nomad",
            "url": "https://dejavunomad.com"
          },
          "about": {
            "@type": "Thing",
            "name": "Travel Deals"
          }
        }}
      />
      
      <Box sx={documentPageCanvasSx}>
      <DocumentMarketingHero
        eyebrow={t('nav.packages')}
        title="Plan your journey with ease"
        subtitle="Flights, hotels, and beyond — in one itinerary."
      />
      <Box
        sx={{
          bgcolor: DOCUMENT.nav,
          borderBottom: `1px solid ${DOCUMENT.navRule}`,
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 5 } }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => {
              setActiveTab(newValue);
              navigate(`/deals?tab=${newValue}`, { replace: true });
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={documentChromeTabsSx}
          >
            <Tab label="Destinations" />
            <Tab label="Road Trips" />
            <Tab label="Flights" />
            <Tab label="Stays" />
            <Tab label="Things to do" />
            <Tab label="Events" />
            <Tab label="Packages" />
          </Tabs>
        </Container>
      </Box>
      <Container maxWidth="xl" sx={{ py: 4 }}>

      {/* Search Card - Context-specific fields based on active tab (not shown for Destinations tab) */}
      {activeTab > 1 && (
      <Card sx={{ mb: 4, borderRadius: '0 0 12px 12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderTop: 'none', overflow: 'visible' }}>
        <CardContent sx={{ p: { xs: 2, md: 3 }, overflow: 'visible' }}>
          {/* FLIGHTS TAB - Trip Type Selection */}
          {activeTab === 2 && (
            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
              <Button
                variant={tripType === 'one_way' ? 'contained' : 'outlined'}
                onClick={() => setTripType('one_way')}
                sx={documentToggleBtnSx(tripType === 'one_way')}
              >
                One Way
              </Button>
              <Button
                variant={tripType === 'round_trip' ? 'contained' : 'outlined'}
                onClick={() => setTripType('round_trip')}
                sx={documentToggleBtnSx(tripType === 'round_trip')}
              >
                Round Trip
              </Button>
            </Box>
          )}

      {/* Input Fields Section - Single Row Layout */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          flexWrap: 'wrap'
        }}>
          {/* FROM field - Only for Flights (tab 0) and Packages (tab 4) */}
          {(activeTab === 2 || activeTab === 6) && (
          <Autocomplete
            multiple
            freeSolo
            options={originOptions || []}
            value={typeof origin === 'string' ? origin.split(',').map(s => s.trim()).filter(Boolean) : (Array.isArray(origin) ? origin : [])}
            onInputChange={(event, newInputValue, reason) => {
              // Search for airports as user types
              if (newInputValue && newInputValue.length >= 2) {
                handleOriginAutocomplete(newInputValue);
              }
            }}
            onChange={(event, newValue, reason) => {
              // newValue is array of selected items (chips)
              if (Array.isArray(newValue)) {
                // Process each value - extract IATA code or convert city name
                const iatas = newValue.map(item => {
                  // If it's an object from dropdown (e.g., {label: "SFO - San Francisco", iata: "SFO"})
                  if (typeof item === 'object' && item.iata) {
                    return item.iata.toUpperCase();
                  } else if (typeof item === 'object' && item.value) {
                    return item.value.toUpperCase();
                  } else if (typeof item === 'string') {
                    const trimmed = item.trim();
                    // Try to extract IATA from label like "SFO - San Francisco"
                    const iataMatch = trimmed.match(/^([A-Z]{3})\s*-/i);
                    if (iataMatch) {
                      return iataMatch[1].toUpperCase();
                    }
                    // If it's already a 3-letter code, uppercase it
                    if (trimmed.length === 3 && /^[a-z]{3}$/i.test(trimmed)) {
                      return trimmed.toUpperCase();
                    }
                    // Otherwise, it's a city name - return as is (backend will handle)
                    return trimmed;
                  }
                  return item;
                }).filter(Boolean);
                
                setOrigin(iatas.join(','));
              } else if (newValue === null || newValue.length === 0) {
                setOrigin('');
              }
            }}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option.label || '';
            }}
            filterOptions={(options, params) => {
              const filtered = options.filter(option => {
                const label = typeof option === 'string' ? option : option.label;
                return label.toLowerCase().includes(params.inputValue.toLowerCase());
              });

              // Only add custom option if:
              // 1. There's input text
              // 2. No matching results from airport database
              // 3. Input is 3 letters (likely IATA) or longer (likely city name)
              const { inputValue } = params;
              const trimmedInput = inputValue.trim();
              
              if (trimmedInput !== '' && filtered.length === 0 && trimmedInput.length >= 3) {
                filtered.push({
                  label: `Add "${trimmedInput}"`,
                  value: trimmedInput,
                  isCustom: true
                });
              }

              return filtered;
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="From (City or IATA)"
                sx={{ 
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { sm: 200 },
                  flex: { xs: 1, sm: '0 0 auto' },
                  '& .MuiInputBase-root': {
                    minHeight: 56
                  }
                }}
                helperText="Type and press Enter"
                error={false}
              />
            )}
            ChipProps={{
              size: 'small',
              color: 'primary',
              variant: 'outlined'
            }}
          />
          )}
          
          {/* TO/WHERE field - Stays uses plain TextField (Events-style free typing) */}
          {activeTab === 3 ? (
            <Box
              sx={{
                position: 'relative',
                width: { xs: '100%', sm: 320 },
                flex: { xs: 1, sm: '0 0 auto' },
                minWidth: { sm: 320 },
              }}
            >
              <TextField
                fullWidth
                inputRef={stayDestinationInputRef}
                label="Where to?"
                value={destination}
                onChange={(e) => {
                  const val = e.target.value;
                  setDestination(val);
                  if (val.trim().length >= 2) {
                    handleDestinationAutocomplete(val);
                    setDestinationSuggestionsOpen(true);
                  } else {
                    setDestinationOptions([]);
                    setDestinationSuggestionsOpen(false);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && destinationSuggestionsOpen && destinationOptions.length > 0) {
                    e.preventDefault();
                    const opt = destinationOptions[0];
                    setDestination(opt.city || opt.value || opt.label);
                    setDestinationOptions([]);
                    setDestinationSuggestionsOpen(false);
                  }
                }}
                onFocus={() => {
                  if (destinationOptions.length > 0 && destination.trim().length >= 2) {
                    setDestinationSuggestionsOpen(true);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => setDestinationSuggestionsOpen(false), 150);
                }}
                helperText="e.g., Orlando, Tokyo"
                sx={{ '& .MuiInputBase-root': { minHeight: 56 } }}
              />
              <Popper
                open={destinationSuggestionsOpen && destinationOptions.length > 0}
                anchorEl={stayDestinationInputRef.current}
                placement="bottom-start"
                sx={{ zIndex: 1400 }}
                modifiers={[
                  {
                    name: 'sameWidth',
                    enabled: true,
                    phase: 'beforeWrite',
                    requires: ['computeStyles'],
                    fn: ({ state }) => {
                      state.styles.popper.width = `${state.rects.reference.width}px`;
                    },
                  },
                ]}
              >
                <Paper
                  elevation={4}
                  sx={{
                    maxHeight: 240,
                    overflow: 'auto',
                    mt: 0.5,
                  }}
                >
                  {destinationOptions.map((opt, index) => (
                    <Box
                      key={opt.placeId || opt.label || index}
                      component="button"
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        setDestination(opt.city || opt.value || opt.label);
                        setDestinationOptions([]);
                        setDestinationSuggestionsOpen(false);
                      }}
                      sx={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        px: 2,
                        py: 1.25,
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                        {opt.label}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Popper>
            </Box>
          ) : (
          <Autocomplete
            key={`dest-${(activeTab === 2 || activeTab === 6) ? 'multi' : 'single'}`}
            sx={{
              width: { xs: '100%', sm: (activeTab === 2 || activeTab === 6) ? 'auto' : 320 },
              flex: { xs: 1, sm: '0 0 auto' },
              minWidth: { sm: (activeTab === 2 || activeTab === 6) ? 200 : 320 },
            }}
            autoHighlight={false}
            autoSelect={false}
            openOnFocus={activeTab === 2 || activeTab === 6}
            onOpen={() => setDestinationAutocompleteOpen(true)}
            onClose={() => setDestinationAutocompleteOpen(false)}
            multiple={activeTab === 2 || activeTab === 6}
            freeSolo
            options={(activeTab === 4 || activeTab === 5) ? [] : (destinationOptions || [])}
            value={
              (activeTab === 2 || activeTab === 6)
                ? (typeof destination === 'string' ? destination.split(',').map(s => s.trim()).filter(Boolean) : (Array.isArray(destination) ? destination : []))
                : (destination || '')
            }
            inputValue={(activeTab === 2 || activeTab === 6) ? undefined : (destination || '')}
            onInputChange={(event, newInputValue, reason) => {
              // For single-select tabs (Stays, Things to do, Events)
              if (activeTab !== 2 && activeTab !== 6) {
              if (reason === 'input' || reason === 'clear') {
                  let processedValue = newInputValue;
                  // Convert IATA to city name if it's a 3-letter code — only for Stays tab (tab 2)
                  if (activeTab === 3) {
                    const trimmedValue = newInputValue.trim();
                    if (trimmedValue.length === 3 && /^[A-Z]{3}$/i.test(trimmedValue)) {
                      processedValue = iataToCity(trimmedValue);
                    }
                  }
                  setDestination(processedValue);
                  if (activeTab === 3 || activeTab === 4) {
                    handleDestinationAutocomplete(processedValue);
                  }
                }
              } else {
                // For multi-select tabs (Flights, Packages)
                if (newInputValue && newInputValue.length >= 2) {
                handleDestinationAutocomplete(newInputValue);
              }
              }
            }}
            onChange={(event, newValue, reason) => {
              // Multi-select for Flights/Packages
              if (activeTab === 2 || activeTab === 6) {
                if (Array.isArray(newValue)) {
                  const iatas = newValue.map(item => {
                    if (typeof item === 'object' && item.iata) {
                      return item.iata.toUpperCase();
                    } else if (typeof item === 'object' && item.value) {
                      return item.value.toUpperCase();
                    } else if (typeof item === 'string') {
                      const trimmed = item.trim();
                      const iataMatch = trimmed.match(/^([A-Z]{3})\s*-/i);
                      if (iataMatch) {
                        return iataMatch[1].toUpperCase();
                      }
                      if (trimmed.length === 3 && /^[a-z]{3}$/i.test(trimmed)) {
                        return trimmed.toUpperCase();
                      }
                      return trimmed;
                    }
                    return item;
                  }).filter(Boolean);
                  
                  setDestination(iatas.join(','));
                } else if (newValue === null || newValue.length === 0) {
                  setDestination('');
                }
              } else {
                // Single-select for Stays/Things to do/Events
              if (newValue && typeof newValue === 'object' && newValue.value) {
                const selectedDestination = (activeTab === 3 || activeTab === 4)
                  ? (newValue.city || newValue.value)
                  : newValue.value;
                setDestination(selectedDestination);
              } else if (typeof newValue === 'string') {
                  const trimmedValue = newValue.trim();
                  if (activeTab === 3 && trimmedValue.length === 3 && /^[A-Z]{3}$/i.test(trimmedValue)) {
                    const cityName = iataToCity(trimmedValue);
                    setDestination(cityName);
                  } else {
                setDestination(newValue);
                  }
                }
              }
            }}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option.label || '';
            }}
            filterOptions={
              (activeTab === 2 || activeTab === 6)
                ? (options, params) => {
                    const filtered = options.filter(option => {
                      const label = typeof option === 'string' ? option : option.label;
                      return label.toLowerCase().includes(params.inputValue.toLowerCase());
                    });

                    // Only add custom option if:
                    // 1. There's input text
                    // 2. No matching results from airport database
                    // 3. Input is 3 letters (likely IATA) or longer (likely city name)
                    const { inputValue } = params;
                    const trimmedInput = inputValue.trim();
                    
                    if (trimmedInput !== '' && filtered.length === 0 && trimmedInput.length >= 3) {
                      filtered.push({
                        label: `Add "${trimmedInput}"`,
                        value: trimmedInput,
                        isCustom: true
                      });
                    }

                    return filtered;
                  }
                : undefined
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={activeTab === 2 ? "To (City or IATA)" : activeTab === 6 ? "To (City or IATA)" : "Where to?"}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-root': {
                    minHeight: 56
                  }
                }}
                inputProps={{
                  ...params.inputProps,
                  onKeyDown: (event) => {
                    const hasSuggestions = destinationAutocompleteOpen || destinationOptions.length > 0;
                    if (event.key === ' ' && hasSuggestions) {
                      event.stopPropagation();
                      return;
                    }
                    params.inputProps?.onKeyDown?.(event);
                  },
                }}
                helperText={
                  (activeTab === 2 || activeTab === 6) 
                    ? "Type and press Enter" 
                    : "e.g., Orlando, Tokyo"
                }
                error={false}
              />
            )}
            ChipProps={
              (activeTab === 2 || activeTab === 6)
                ? {
                    size: 'small',
                    color: 'primary',
                    variant: 'outlined'
                  }
                : undefined
            }
          />
          )}
          <TextField
            label="Depart"
            type="date"
            value={departDate}
            onChange={(e) => {
              const newDepartDate = e.target.value;
              setDepartDate(newDepartDate);
              
              // If return date is before new departure date, adjust it
              if (returnDate && newDepartDate > returnDate) {
                setReturnDate(newDepartDate);
              }
            }}
            sx={{ 
              width: { xs: '100%', sm: 140 },
              flex: { xs: 1, sm: '0 0 auto' },
              '& .MuiInputBase-root': {
                height: 56
              }
            }}
            InputLabelProps={{ shrink: true }}
          />
          {/* Only show return date for round trips or non-flight tabs */}
          {(activeTab !== 0 || tripType === 'round_trip') && (
          <TextField
            label="Return"
            type="date"
            value={returnDate}
            onChange={(e) => setReturnDate(e.target.value)}
            sx={{ 
              width: { xs: '100%', sm: 140 },
              flex: { xs: 1, sm: '0 0 auto' },
              '& .MuiInputBase-root': {
                height: 56
              }
            }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 
              min: departDate || new Date().toISOString().split('T')[0]
            }}
          />
          )}
          <NumericTextField
            label="Passengers"
            value={passengers}
            onValueChange={setPassengers}
            min={1}
            max={9}
            sx={{ 
              width: { xs: '100%', sm: 100 },
              flex: { xs: 1, sm: '0 0 auto' },
              '& .MuiInputBase-root': {
                height: 56
              }
            }}
            InputLabelProps={{ shrink: true }}
          />
          {activeTab === 3 && (
            <NumericTextField
              label={t('stays.radiusMiles')}
              value={staysRadiusMiles}
              onValueChange={setStaysRadiusMiles}
              min={1}
              max={100}
              sx={{
                width: { xs: '100%', sm: 120 },
                flex: { xs: 1, sm: '0 0 auto' },
                '& .MuiInputBase-root': {
                  height: 56,
                },
              }}
              InputLabelProps={{ shrink: true }}
              helperText={t('stays.radiusHelper')}
            />
          )}
          <Button 
            variant="contained" 
            onClick={handleSearchMatrix} 
            disabled={matrixLoading}
            sx={{ 
              ...partnerOxbloodBtnSx,
              width: { xs: '100%', sm: 'auto' },
              minWidth: { xs: 'auto', sm: 120 },
              height: 56,
              flex: { xs: 1, sm: '0 0 auto' },
            }}
          >
            {matrixLoading ? 'Loading...' : activeTab === 6 ? 'Search Packages' : 'Search'}
          </Button>
        </Box>
        </CardContent>
      </Card>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
      )}

      {/* Matrix Results Section */}
      {matrix.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Price Matrix Results
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {matrix.slice(0, 10).map((item, index) => (
              <Card key={index} sx={{ minWidth: 200 }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary">
                    {item.date}
                  </Typography>
                  <Typography variant="h6" color="primary">
                    ${item.price}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {/* Content Area - Results display below search */}
      {!loading && !error && (
        <Box>

          {/* Destinations Tab Content */}
          {activeTab === 0 && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>Explore Destinations</Typography>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/destinations')}
                  sx={partnerOutlineBtnSx}
                >
                  View All Destinations
                </Button>
              </Box>

              {/* Continent filters */}
              <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                <Chip
                  label="All"
                  onClick={() => setDestinationsContinent('')}
                  sx={documentFilterChipSx(destinationsContinent === '')}
                />
                {destinationsContinents.map((c) => (
                  <Chip
                    key={c.continent}
                    label={`${c.continent} (${c.count})`}
                    onClick={() => setDestinationsContinent(destinationsContinent === c.continent ? '' : c.continent)}
                    sx={documentFilterChipSx(destinationsContinent === c.continent)}
                  />
                ))}
              </Box>

              {/* Search */}
              <TextField
                fullWidth
                size="small"
                placeholder="Search destinations..."
                value={destinationsSearch}
                onChange={(e) => setDestinationsSearch(e.target.value)}
                sx={{ mb: 3, maxWidth: 400 }}
              />

              {destinationsLoading ? (
                <Grid container spacing={2.75}>
                  {[...Array(6)].map((_, i) => (
                    <Grid item xs={12} sm={6} md={4} key={i}>
                      <Card sx={{ borderRadius: 0, bgcolor: DOCUMENT.surface, border: `1px solid ${DOCUMENT.ruleHard}`, boxShadow: 'none' }}>
                        <Box sx={{ aspectRatio: '16 / 9', bgcolor: DOCUMENT.rule }} />
                        <CardContent>
                          <Box sx={{ height: 20, bgcolor: DOCUMENT.rule, mb: 1, width: '60%' }} />
                          <Box sx={{ height: 14, bgcolor: DOCUMENT.rule, width: '40%' }} />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Grid container spacing={2.75}>
                  {destinationsData
                    .filter(d => {
                      const matchesContinent = !destinationsContinent || d.continent === destinationsContinent;
                      const matchesSearch = !destinationsSearch || 
                        d.name.toLowerCase().includes(destinationsSearch.toLowerCase()) ||
                        d.country.toLowerCase().includes(destinationsSearch.toLowerCase());
                      return matchesContinent && matchesSearch;
                    })
                    .map((dest) => (
                    <Grid item xs={12} sm={6} md={4} key={dest.id}>
                      <DocumentPackageCard
                        imageUrl={dest.image_url || `https://source.unsplash.com/400x300/?${encodeURIComponent(dest.name)},travel`}
                        imageAlt={dest.name}
                        photographerName={dest.unsplash_photographer_name}
                        photographerUsername={dest.unsplash_photographer_username}
                        badges={[dest.continent]}
                        coverLabel={dest.continent}
                        eyebrow={dest.continent}
                        title={dest.name}
                        location={dest.country}
                        description={dest.short_description}
                        rows={[
                          { label: t('packagesPage.destination'), value: dest.country },
                        ]}
                        actionLabel={t('packagesPage.openDestination')}
                        onClick={() => navigate(`/destinations/${dest.slug}`)}
                      />
                    </Grid>
                  ))}
                  {destinationsData.filter(d => {
                    const matchesContinent = !destinationsContinent || d.continent === destinationsContinent;
                    const matchesSearch = !destinationsSearch ||
                      d.name.toLowerCase().includes(destinationsSearch.toLowerCase()) ||
                      d.country.toLowerCase().includes(destinationsSearch.toLowerCase());
                    return matchesContinent && matchesSearch;
                  }).length === 0 && (
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Typography variant="h6" color="text.secondary">
                          No destinations found matching your criteria
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              )}
            </Box>
          )}

          {activeTab === 2 && (
            <Box>
              {/* Flights Section */}
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Flights</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Search for flights using the form above, or explore popular routes below.
              </Typography>

              <Grid container spacing={2.75} sx={{ mb: 4 }}>
                {FLIGHT_PROMO_CARDS.map((card, i) => (
                  <Grid item xs={12} sm={6} md={3} key={i}>
                    <DocumentPackageCard
                      imageUrl={card.image}
                      imageAlt={card.title}
                      coverLabel={card.cta}
                      eyebrow={t('nav.flights', { defaultValue: 'Flights' })}
                      title={card.title}
                      description={card.subtitle}
                    />
                  </Grid>
                ))}
              </Grid>

              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Popular Routes</Typography>
              <Grid container spacing={2.75}>
                {POPULAR_FLIGHT_ROUTES.map((route, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <DocumentPackageCard
                      imageUrl={route.image}
                      imageAlt={route.to}
                      badges={[route.fromCode, route.toCode]}
                      coverLabel={t('packagesPage.fromPrice', { price: route.price })}
                      eyebrow={t('packagesPage.route')}
                      title={`${route.from} → ${route.to}`}
                      location={`${route.fromCode} → ${route.toCode}`}
                      rows={[
                        { label: t('packagesPage.route'), value: `${route.fromCode} → ${route.toCode}` },
                        { label: t('packagesPage.price'), value: t('packagesPage.fromPrice', { price: route.price }) },
                      ]}
                      actionLabel={t('packagesPage.openRoute')}
                      onClick={() => {
                        setOrigin(route.fromCode);
                        setDestination(route.toCode);
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}


          {activeTab === 3 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>{t('stays.title')}</Typography>
                {destination && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => loadHotelsForTab({ refresh: true })}
                    disabled={duffelLoading || staysBackgroundLoading}
                    sx={partnerOutlineBtnSx}
                  >
                    {duffelLoading || staysBackgroundLoading ? t('stays.refreshing') : t('stays.refresh')}
                  </Button>
                )}
              </Box>
              {destination && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {passengers > 1
                    ? t('stays.showingFor', {
                        destination: stayDestinationDisplay,
                        checkIn: departDate,
                        checkOut: returnDate,
                        guests: passengers,
                        radius: staysRadiusMiles,
                      })
                    : t('stays.showingForSingular', {
                        destination: stayDestinationDisplay,
                        checkIn: departDate,
                        checkOut: returnDate,
                        radius: staysRadiusMiles,
                      })}
                </Typography>
              )}
              <Alert severity="info" sx={{ mb: 3, '& .MuiAlert-message': { whiteSpace: 'normal', overflow: 'visible' } }}>
                {t('stays.tip')}
              </Alert>
              {duffelLoading && !hasStaySearchResults && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>{t('stays.searching')}</Typography>
                </Box>
              )}
              {(staysPendingProviders.length > 0 || staysBackgroundLoading) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    {staysPendingProviders.length > 0
                      ? t('stays.searchingProviders', {
                          providers: staysPendingProviders
                            .map((providerId) => t(`stays.providers.${providerId}`))
                            .join(', '),
                        })
                      : t('stays.loadingMore')}
                  </Typography>
                </Box>
              )}
              {destination && staysSearchGeneration > 0 && (
                <Box>
                  {(mappableStayCount > 0 || showStaysMap) && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        {showStaysMap
                          ? staysMapBounds
                            ? t('stays.mapVisibleListingsCount', { count: mapVisibleStayListings.length })
                            : t('stays.mapLoadingArea')
                          : t('stays.mapListingsCount', { count: mappableStayCount })}
                      </Typography>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={staysViewMode}
                        onChange={(_, value) => value && setStaysViewMode(value)}
                        aria-label={t('stays.viewMode')}
                        sx={{
                          '& .MuiToggleButton-root': {
                            ...documentToggleBtnSx(false),
                            borderRadius: 0,
                            '&.Mui-selected': documentToggleBtnSx(true),
                          },
                        }}
                      >
                        <ToggleButton value="map" aria-label={t('stays.viewMap')}>
                          <MapIcon sx={{ mr: 0.5, fontSize: 18 }} />
                          {t('stays.viewMap')}
                        </ToggleButton>
                        <ToggleButton value="list" aria-label={t('stays.viewList')}>
                          <ViewListIcon sx={{ mr: 0.5, fontSize: 18 }} />
                          {t('stays.viewList')}
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  )}
                  {showStaysMap && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {t('stays.mapClickHint')}
                    </Typography>
                  )}
                  {showStaysMap && (
                    <StaysMapView
                      listings={allStayListings}
                      initialDestination={stayDestinationDisplay}
                      initialRadiusMiles={staysRadiusMiles}
                      searchKey={staysSearchGeneration}
                      onViewportChange={handleStaysViewportChange}
                      onSearchArea={handleStaysSearchArea}
                    />
                  )}
                  {showStaysList && rentalStayListings.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                        {t('stays.vacationRentals')}
                      </Typography>
                      <Grid container spacing={3}>
                        {rentalStayListings.map((listing) => (
                          <StayListingCard
                            key={listing.id}
                            listing={listing}
                            viewOnProviderLabel={t('stays.viewOnProvider', {
                              provider: listing.providerLabel || t(`stays.providers.${listing.provider}`),
                            })}
                            bookNowLabel={t('stays.bookNow')}
                          />
                        ))}
                      </Grid>
                    </Box>
                  )}

                  {showStaysList && hotelStayListings.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                        {t('stays.hotels')}
                      </Typography>
                      <Grid container spacing={3}>
                        {hotelStayListings.map((listing) => (
                          <StayListingCard
                            key={listing.id}
                            listing={listing}
                            viewOnProviderLabel={t('stays.viewOnProvider', {
                              provider: listing.providerLabel || t(`stays.providers.${listing.provider}`),
                            })}
                            bookNowLabel={t('stays.bookNow')}
                          />
                        ))}
                      </Grid>
                    </Box>
                  )}
                  {showStaysMap && mapVisibleRentalListings.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                        {t('stays.vacationRentals')}
                      </Typography>
                      <Grid container spacing={3}>
                        {mapVisibleRentalListings.map((listing) => (
                          <StayListingCard
                            key={listing.id}
                            listing={listing}
                            viewOnProviderLabel={t('stays.viewOnProvider', {
                              provider: listing.providerLabel || t(`stays.providers.${listing.provider}`),
                            })}
                            bookNowLabel={t('stays.bookNow')}
                          />
                        ))}
                      </Grid>
                    </Box>
                  )}
                  {showStaysMap && mapVisibleHotelListings.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                        {t('stays.hotels')}
                      </Typography>
                      <Grid container spacing={3}>
                        {mapVisibleHotelListings.map((listing) => (
                          <StayListingCard
                            key={listing.id}
                            listing={listing}
                            viewOnProviderLabel={t('stays.viewOnProvider', {
                              provider: listing.providerLabel || t(`stays.providers.${listing.provider}`),
                            })}
                            bookNowLabel={t('stays.bookNow')}
                          />
                        ))}
                      </Grid>
                    </Box>
                  )}
                  {showStaysMap && mapVisibleStayListings.length === 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('stays.mapNoListingsInView')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
              {staysSearchCompleted && !duffelLoading && !staysBackgroundLoading && staysPendingProviders.length === 0 && destination && stayProviderFallbacks.length > 0 && (
                <Box sx={{ mt: hasStaySearchResults ? 2 : 0, mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('stays.providerFallbackHint')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {stayProviderFallbacks.map((listing) => (
                      <Button
                        key={listing.id}
                        variant="outlined"
                        size="small"
                        href={listing.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={partnerOutlineBtnSx}
                      >
                        {t('stays.viewOnProvider', {
                          provider: listing.providerLabel || t(`stays.providers.${listing.provider}`),
                        })}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}
              {staysSearchCompleted && !duffelLoading && !staysBackgroundLoading && staysPendingProviders.length === 0 && destination && !hasStaySearchResults && stayProviderFallbacks.length === 0 && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  {t('stays.noResults')}
                </Alert>
              )}
              {!duffelLoading && !destination && (
                <Box>
                  {/* Default: Explore stays in popular destinations */}
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Explore stays in popular destinations</Typography>
                  <Grid container spacing={2.75} sx={{ mb: 4 }}>
                    {POPULAR_STAY_DESTINATIONS.map((dest, i) => (
                      <Grid item xs={12} sm={6} md={4} key={i}>
                        <DocumentPackageCard
                          imageUrl={dest.image}
                          imageAlt={dest.city}
                          badges={[dest.tag]}
                          coverLabel={dest.tag}
                          eyebrow={dest.tag}
                          title={dest.city}
                          location={dest.country}
                          rows={[
                            { label: t('packagesPage.destination'), value: dest.country },
                          ]}
                          actionLabel={t('packagesPage.openStay')}
                          onClick={() => setDestination(dest.city)}
                        />
                      </Grid>
                    ))}
                  </Grid>

                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Featured Hotels</Typography>
                  <Grid container spacing={2.75}>
                    {getBookingFallback('general').map((hotel, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <DocumentPackageCard
                          imageUrl={hotel.image}
                          imageAlt={hotel.name}
                          badges={[t('stays.typeHotel')]}
                          coverLabel={hotel.price}
                          eyebrow={t('stays.typeHotel')}
                          title={hotel.name}
                          location={hotel.location}
                          rows={[
                            { label: t('packagesPage.rating'), value: `${hotel.rating}/5` },
                            { label: t('packagesPage.price'), value: hotel.price },
                          ]}
                          actionLabel={t('stays.bookNow')}
                          onClick={() => window.open(hotel.booking_url, '_blank', 'noopener,noreferrer')}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          )}

          {activeTab === 4 && (
            <Box>
              {/* Things to do Section */}
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Things to do</Typography>
              {destination && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Showing activities for {destination}
                </Typography>
              )}
              <Alert severity="info" sx={{ mb: 3 }}>
                💡 <strong>Tip:</strong> Book your activities on Viator, then create a trip and add them to your itinerary to keep track of all your plans!
              </Alert>
              {viatorLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>Searching for activities...</Typography>
                </Box>
              )}
              {viatorActivities.length > 0 ? (
                <Box>
                  <Grid container spacing={2.75}>
                    {viatorActivities.map((activity) => {
                      const durationValue = activity.duration
                        ? (typeof activity.duration === 'object'
                          ? (activity.duration.fixedDurationInMinutes
                            ? `${Math.round(activity.duration.fixedDurationInMinutes / 60)} hours`
                            : `${Math.round(activity.duration.variableDurationFromMinutes / 60)}-${Math.round(activity.duration.variableDurationToMinutes / 60)} hours`)
                          : activity.duration)
                        : t('packagesPage.duration');
                      return (
                    <Grid item xs={12} sm={6} md={4} key={activity.id}>
                      <DocumentPackageCard
                        imageUrl={activity.image}
                        imageAlt={activity.title}
                        coverLabel={activity.price}
                        eyebrow={t('packagesPage.openActivity')}
                        title={activity.title}
                        description={activity.description}
                        rows={[
                          { label: t('packagesPage.duration'), value: durationValue },
                          { label: t('packagesPage.rating'), value: activity.rating },
                          { label: t('packagesPage.price'), value: activity.price },
                        ]}
                        actionLabel={t('packagesPage.openActivity')}
                        onClick={() => window.open(activity.url, '_blank', 'noopener,noreferrer')}
                      />
                    </Grid>
                      );
                    })}
                  </Grid>
                  
                  {/* See more on Viator button */}
                  {viatorCityUrl && (
                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                      <Button
                        variant="outlined"
                        size="large"
                        onClick={() => window.open(viatorCityUrl, '_blank', 'noopener,noreferrer')}
                        sx={partnerOutlineBtnSx}
                      >
                        See more activities on Viator →
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : !viatorLoading && (
                <Box>
                  {destination ? (
                    <Typography color="text.secondary">
                      No activities found for {destination}. Try a different destination.
                    </Typography>
                  ) : (
                    <>
                      {/* Category browsing */}
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Browse by category</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
                        {ACTIVITY_CATEGORIES.map((cat, i) => (
                          <Chip
                            key={i}
                            label={`${cat.icon}  ${cat.label}`}
                            sx={documentFilterChipSx(false)}
                          />
                        ))}
                      </Box>

                      {/* Popular activity destinations */}
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Popular destinations for activities</Typography>
                      <Grid container spacing={2.75}>
                        {POPULAR_ACTIVITY_DESTINATIONS.map((dest, i) => (
                          <Grid item xs={12} sm={6} md={4} key={i}>
                            <DocumentPackageCard
                              imageUrl={dest.image}
                              imageAlt={dest.city}
                              badges={dest.tags.slice(0, 2)}
                              coverLabel={dest.tags[0]}
                              eyebrow={dest.country}
                              title={dest.city}
                              location={dest.country}
                              description={dest.tags.join(' · ')}
                              rows={[
                                { label: t('packagesPage.destination'), value: dest.country },
                              ]}
                              actionLabel={t('packagesPage.openActivity')}
                              onClick={() => setDestination(dest.city)}
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </>
                  )}
                </Box>
              )}
            </Box>
          )}

          {activeTab === 5 && (
            <Box>
              {/* Events Section */}
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Event Guides</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {(() => {
                  const resolved = destination ? getCityName(destination) : null;
                  const dateStr = departDate && returnDate ? ` from ${departDate} to ${returnDate}` : departDate ? ` from ${departDate}` : '';
                  if (!resolved && !dateStr) return 'Showing all upcoming events & experiences';
                  if (resolved && dateStr) return `Events near ${resolved}${dateStr}`;
                  if (resolved) return `Events near ${resolved}`;
                  return `Events${dateStr}`;
                })()}
              </Typography>
              <Alert severity="info" sx={{ mb: 3 }}>
                💡 <strong>Tip:</strong> Find an event you like? Open the guide for hotel, flight, restaurant, and activity recommendations — then create a trip from it!
              </Alert>
              {events.length === 0 ? (
                <Typography color="text.secondary">
                  {destination && (departDate || returnDate)
                    ? `No events found near "${getCityName(destination)}" in that date range. Try widening your dates or searching a broader area (e.g. "USA" or "Europe").`
                    : destination
                    ? `No events found for "${getCityName(destination)}". Try a broader search like a country name or category (e.g. "soccer", "cruise", "festival").`
                    : 'Showing all upcoming events & experiences.'}
                </Typography>
              ) : (
                <Grid container spacing={2.75}>
                  {events.map((ev) => {
                    const catMap = {
                      sport: { label: 'Sport', emoji: '🏆' },
                      music: { label: 'Music', emoji: '🎵' },
                      cultural: { label: 'Cultural', emoji: '🎭' },
                      food: { label: 'Food', emoji: '🍴' },
                      theme_park: { label: 'Theme Park', emoji: '🎢' },
                      city_experience: { label: 'City', emoji: '🏙️' },
                      layover: { label: 'Layover', emoji: '✈️' },
                      nature: { label: 'Nature', emoji: '🌿' },
                      festival: { label: 'Festival', emoji: '🎉' },
                      expo: { label: 'Expo', emoji: '🎪' },
                      parade: { label: 'Parade', emoji: '🎊' },
                      seasonal: { label: 'Seasonal', emoji: '🌸' },
                      cruise: { label: 'Cruise', emoji: '🚢' },
                    };
                    const cat = catMap[ev.category] || { label: ev.category, emoji: '📌' };
                    const budgetValue = (ev.budget_estimate_low || ev.budget_estimate_high)
                      ? `${ev.budget_currency || 'USD'} ${ev.budget_estimate_low ? Math.round(ev.budget_estimate_low).toLocaleString() : '?'} – ${ev.budget_estimate_high ? Math.round(ev.budget_estimate_high).toLocaleString() : '?'}`
                      : null;
                    return (
                    <Grid item xs={12} sm={6} md={4} key={ev.id}>
                      <DocumentPackageCard
                        imageUrl={ev.cover_image || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1200&q=80'}
                        imageAlt={ev.title}
                        badges={[`${cat.emoji} ${cat.label}`]}
                        coverLabel={cat.label}
                        eyebrow={cat.label}
                        title={ev.title}
                        location={ev.location_name}
                        description={ev.short_description || ev.typical_dates}
                        rows={[
                          ev.next_event_date
                            ? { label: t('packagesPage.date'), value: formatDatePretty(ev.next_event_date) }
                            : null,
                          ev.typical_dates
                            ? { label: t('packagesPage.typicalDates'), value: ev.typical_dates }
                            : null,
                          budgetValue
                            ? { label: t('packagesPage.budget'), value: budgetValue }
                            : null,
                        ].filter(Boolean)}
                        actionLabel={t('packagesPage.openEvent')}
                        onClick={() => navigate(`/event-guides/${ev.slug}`)}
                      />
                    </Grid>
                    );
                  })}
                </Grid>
              )}
            </Box>
          )}

          {activeTab === 6 && (
            <Box>
              {/* Packages Section */}
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Travel Packages</Typography>
              
              {/* Sub-tabs: Agent | Private | Build Your Own */}
              <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={t('packagesPage.agentPackagesTab')}
                  onClick={() => setPackagesSubTab('agent')}
                  sx={documentFilterChipSx(packagesSubTab === 'agent')}
                />
                {privateTravelAvailable ? (
                  <Chip
                    label={t('packagesPage.privatePackagesTab')}
                    onClick={() => setPackagesSubTab('private')}
                    sx={documentFilterChipSx(packagesSubTab === 'private')}
                    data-testid="packages-subtab-private"
                  />
                ) : null}
                <Chip
                  label={t('packagesPage.buildYourOwnTab')}
                  onClick={() => setPackagesSubTab('build')}
                  sx={documentFilterChipSx(packagesSubTab === 'build')}
                />
              </Box>
              
              {/* Agent Packages Section */}
              {packagesSubTab === 'agent' && (
                <Box>
                  <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                    Browse curated travel packages from verified travel agents. Book a complete itinerary with accommodations, activities, and more!
                  </Typography>

                  <PackageAgentFilter
                    agents={packageAgents}
                    value={selectedPackageAgentId}
                    onChange={setSelectedPackageAgentId}
                  />
                  
                  {partnerPackagesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress />
                    </Box>
                  ) : visiblePartnerPackages.length > 0 ? (
                    <Grid container spacing={2.75}>
                      {visiblePartnerPackages.map((pkg) => (
                        <Grid item xs={12} sm={6} md={4} key={pkg.id}>
                          <MarketplacePackageCard
                            pkg={pkg}
                            onOfferedByClick={setPackageAgentDetails}
                            onOpen={(item) => setPreviewPackage({ slug: item.slug, title: item.title })}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box textAlign="center" py={8} sx={{ bgcolor: 'grey.50', borderRadius: 3 }}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        📦 No packages available yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Check back soon for curated travel packages from our partner agents!
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => setPackagesSubTab('build')}
                        sx={partnerOutlineBtnSx}
                      >
                        Build Your Own Package
                      </Button>
                    </Box>
                  )}
                </Box>
              )}

              {privateTravelAvailable && packagesSubTab === 'private' && (
                <Box data-testid="packages-private-section">
                  <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary', maxWidth: 720 }}>
                    {t('packagesPage.privatePackagesIntro')}
                  </Typography>
                  <Box sx={{ mb: 3 }}>
                    <Button
                      onClick={openPrivateJourneyRequest}
                      disabled={privateRequestBusy}
                      sx={partnerOxbloodBtnSx}
                      data-testid="packages-private-request-cta"
                    >
                      {t('packagesPage.requestPrivateJourney')}
                    </Button>
                  </Box>
                  {privatePackagesLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                      <CircularProgress />
                    </Box>
                  ) : privatePackages.length > 0 ? (
                    <Grid container spacing={2.75}>
                      {privatePackages.map((pkg) => (
                        <Grid item xs={12} sm={6} md={4} key={pkg.id}>
                          <MarketplacePackageCard
                            pkg={{
                              ...pkg,
                              is_private_journey: true,
                              illustrative_budget_label: pkg.illustrative_budget_label,
                            }}
                            onOfferedByClick={setPackageAgentDetails}
                            onOpen={(item) => setPreviewPackage({ slug: item.slug, title: item.title })}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Box textAlign="center" py={8} sx={{ bgcolor: DOCUMENT.surface, border: `1px solid ${DOCUMENT.ruleHard}` }}>
                      <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 24, mb: 1 }}>
                        {t('packagesPage.privatePackagesEmpty')}
                      </Typography>
                      <Typography sx={{ color: DOCUMENT.ink3, mb: 3, maxWidth: 420, mx: 'auto' }}>
                        {t('packagesPage.privatePackagesEmptyHint')}
                      </Typography>
                      <Button
                        onClick={openPrivateJourneyRequest}
                        disabled={privateRequestBusy}
                        sx={partnerOxbloodBtnSx}
                      >
                        {t('packagesPage.requestPrivateJourney')}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
              
              {/* Build Your Own Section */}
              {packagesSubTab === 'build' && (
                <Box>
              <Alert severity="info" sx={{ mb: 3 }}>
                💡 <strong>Tip:</strong> Book your flights and hotels as a package, then create a trip and add all the details to your itinerary for easy planning!
              </Alert>
              
              {/* Check if this is a custom search result or default packages */}
              {vacationPackages.length > 0 ? (
                <Box>
                  {vacationPackages[0].type === 'custom' ? (
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                      {origin} → {vacationPackages[0].destination} Package
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                        Use the search form above to find vacation packages. Enter your departure city, destination, and travel dates, then click "Search Packages" to see custom results.
                      </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Popular Destinations {origin && `from ${origin}`}
                  </Typography>
                    </>
                  )}
                  <Grid container spacing={2.75}>
                    {vacationPackages.map((pkg, index) => {
                      const imageUrl = pkg.imageUrl || 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400&h=250&fit=crop&q=80';
                      const durationDays = pkg.duration || pkg.duration_days || 0;
                      const packagePath = `/package/${pkg.id}?origin=${origin || pkg.origin}&destination=${encodeURIComponent(destination || pkg.destination)}&departure_date=${departDate || pkg.depart_date || pkg.departure_date}&return_date=${returnDate || pkg.return_date}&passengers=${passengers || pkg.passengers}&type=${pkg.type}`;
                      const isCustom = pkg.type === 'custom';
                      return (
                        <Grid item xs={12} sm={6} md={4} key={pkg.id || index}>
                          <DocumentPackageCard
                            testId={`traveler-package-card-${pkg.id || index}`}
                            imageUrl={imageUrl}
                            imageAlt={pkg.destination}
                            photographerName={pkg.unsplash_photographer_name}
                            photographerUsername={pkg.unsplash_photographer_username}
                            badges={[pkg.type]}
                            coverLabel={t('packagesPage.durationDays', { count: durationDays })}
                            eyebrow={pkg.type}
                            title={pkg.destination}
                            location={origin || pkg.origin ? `${origin || pkg.origin} → ${pkg.destination}` : pkg.destination}
                            description={[t('packagesPage.allInclusive'), t('packagesPage.bestPrice')].join(' · ')}
                            rows={[
                              { label: t('packagesPage.duration'), value: t('packagesPage.daysCount', { count: durationDays }) },
                              {
                                label: t('packagesPage.price'),
                                value: isCustom ? t('packagesPage.bookSeparately') : `$${pkg.pricing?.total ?? pkg.base_price ?? 0}`,
                                extra: isCustom ? null : t('packagesPage.perPerson'),
                              },
                            ]}
                            actionLabel={t('packagesPage.openPackage')}
                            onClick={() => navigate(packagePath)}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              ) : !loading && (
                <Box textAlign="center" py={8}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No packages found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your search parameters or enter a from location
                  </Typography>
                </Box>
              )}
                </Box>
              )}
            </Box>
          )}

          {activeTab === 1 && (
            <Box sx={{ mt: 3 }}>
              <RoadTripPlanner />
            </Box>
          )}

        </Box>
      )}

      <AgentDetailsDialog
        open={Boolean(packageAgentDetails)}
        onClose={() => setPackageAgentDetails(null)}
        agent={packageAgentDetails}
      />
      <DocumentPageOverlay
        open={Boolean(previewPackage?.slug)}
        onClose={() => setPreviewPackage(null)}
        title={previewPackage?.title || t('packagesPage.openPackage')}
        testId="package-preview-modal"
      >
        {previewPackage?.slug ? (
          <PublicPackageDetails previewSlug={previewPackage.slug} />
        ) : null}
      </DocumentPageOverlay>
      <AdvisorInquiryForm
        open={privateRequestOpen}
        onClose={() => {
          setPrivateRequestOpen(false);
          setPrivateRequestTarget(null);
        }}
        mode="general"
        partnerId={privateRequestTarget?.partnerId}
        partnerName={privateRequestTarget?.partnerName}
        privateRequest
      />
    </Container>
    </Box>
    </>
  );
};

export default DealsPlan;


